// src/App.js
// Lyca 360 - Full Production Version
// Supabase auth + persistent training data + evaluation history

import { useState, useEffect, useRef } from "react";
import * as mammoth from "mammoth";
import { supabase, isSupabaseConfigured } from "./supabaseClient";

// -------------------------------------------------------------
// IMPORTANT: Replace this with your Vercel proxy URL after deploying
// e.g. "https://lyca360-proxy.vercel.app/api/evaluate"
// -------------------------------------------------------------
const PROXY_URL = "YOUR_VERCEL_PROXY_URL/api/evaluate";

// -- Prompt builder --------------------------------------------
const buildSystemPrompt = (examples, jobRole) => {
  const trainingData =
    examples.length === 0
      ? "No training data yet. Use judgment as a seasoned talent evaluator."
      : examples
          .map((ex, i) =>
            `--- Example ${i + 1} | Score: ${ex.score}/100 | Verdict: ${ex.verdict} ---\nResume:\n${ex.resume}\n\nEvaluator Notes:\n${ex.feedback}`
          )
          .join("\n\n");

  return `You are LYCA 360 - a precision resume intelligence engine trained to evaluate candidates exactly as a specific hiring decision-maker would.

EVALUATOR TRAINING HISTORY:
${trainingData}

YOUR MISSION:
Study the patterns, weights, and reasoning from the training history above. Evaluate new resumes with the same instincts, preferences, and standards.

${jobRole ? `ROLE BEING HIRED FOR: ${jobRole}` : ""}

DEEP EVALUATION REQUIREMENTS:
You must carefully analyse each of the following areas for every resume:

1. PROFESSIONAL SUMMARY ANALYSIS
   - Does the summary match the actual experience shown below it?
   - Are the claims in the summary backed by real evidence in the work history?
   - Flag any exaggeration or mismatch between summary and experience.

2. WORK EXPERIENCE TIMELINE VALIDATION
   - Extract every company the candidate worked at and the exact years/dates.
   - Check if the total years of experience adds up correctly.
   - Identify any gaps in employment and flag them.
   - Flag any overlapping dates (working two full-time jobs simultaneously is suspicious).
   - Check if the career progression makes sense (e.g. junior → senior → lead).

3. RESPONSIBILITIES VS ROLE VALIDATION
   - For each company and role, check if the responsibilities listed are realistic for that job title.
   - A "Junior Developer" claiming to have single-handedly architected enterprise systems is suspicious.
   - A "Senior Manager" with no people management responsibilities is suspicious.
   - Flag any responsibilities that don't match the stated role level.

4. TECHNOLOGY AND TOOLS TIMELINE CHECK - THIS IS CRITICAL
   - For each company and the years worked there, check every tool and technology listed.
   - Cross-check against the actual release/availability date of each technology.
   - Examples of what to check:
     * React was released in 2013 - claiming React at a company before 2013 is fraud.
     * Kubernetes was released in 2014 - claiming Kubernetes before 2014 is fraud.
     * Docker was released in 2013 - claiming Docker before 2013 is fraud.
     * AWS was launched in 2006 - claiming AWS before 2006 is fraud.
     * Python Django was released in 2005 - claiming Django before 2005 is fraud.
     * Flutter was released in 2018 - claiming Flutter before 2018 is fraud.
     * ChatGPT/OpenAI API was released in 2022 - claiming it before 2022 is fraud.
     * Kubernetes, Docker, React, Angular, Vue, Flutter, Swift, Kotlin, TypeScript - all have specific release years.
   - If a candidate claims to have used a tool at a company BEFORE that tool existed, this is a major red flag.
   - List every technology anachronism found.

5. SKILL DEPTH CONSISTENCY
   - Does the candidate claim expert-level skills that are not backed by any real project experience?
   - A candidate claiming "10 years React experience" in 2024 is impossible since React launched in 2013.
   - Flag any skill years that exceed the technology's existence.

OUTPUT RULES:
- Output ONLY valid JSON. No markdown. No preamble.
{
  "score": <integer 0-100>,
  "verdict": "<Strong Yes | Yes | Maybe | No | Strong No>",
  "summary": "<2-3 sentences covering overall evaluation>",
  "strengths": ["<specific strength with evidence>", "<specific strength>", "<specific strength>"],
  "concerns": ["<specific concern with evidence>", "<specific concern>"],
  "recommendation": "<one paragraph with clear hire/no-hire reasoning>",
  "standout": "<single most impressive or most alarming finding>",
  "confidence": "<High | Medium | Low>",
  "experience_breakdown": [
    {
      "company": "<company name>",
      "years": "<start year - end year>",
      "role": "<job title>",
      "responsibilities_valid": true,
      "tech_issues": ["<any technology that did not exist during those years>"],
      "notes": "<any concerns about this specific role>"
    }
  ],
  "timeline_issues": ["<any date gaps, overlaps or impossible claims>"],
  "tech_anachronisms": ["<technology claimed before it existed, with details>"]
}`;
};

// -- Authenticity Check Prompt ---------------------------------
const buildAuthenticityPrompt = (resumeText, jobRole) => {
  return `You are LYCA 360's Resume Authenticity Engine - an expert forensic analyst with deep knowledge of:
- Technology release dates and version histories
- Which technologies are compatible or incompatible with each other in real projects
- What technology stacks are realistic for given roles and time periods
- Red flags that indicate a fabricated or inflated resume

RESUME TO ANALYSE:
${resumeText}

${jobRole ? `ROLE BEING EVALUATED FOR: ${jobRole}` : ""}

YOUR JOB:
Perform a deep forensic analysis of this resume. Look for:

1. TIMELINE FRAUD: Does the candidate claim to have used a technology before it was invented or widely available? E.g. claiming React experience in 2010 (React launched 2013), or Kubernetes in 2012 (launched 2014).

2. TECHNOLOGY INCOMPATIBILITY: Do the technologies listed together make no sense in a real project? Real red flags:
   - Java backend + .NET backend on the SAME project (companies pick one)
   - Claims to use React AND Angular AND Vue simultaneously on same project
   - Django + Ruby on Rails on the same project
   - Claims Oracle DBA + MongoDB expertise equally (very different specialisations)
   - AWS + Azure + GCP all listed as primary expertise (pick one in real jobs)
   - Technologies that were competitors listed as co-used (Spring Boot + .NET Core as backend in same stack)

3. ROLE-TECHNOLOGY MISMATCH: Do the technologies claimed match the role title?
   - A "Data Scientist" who lists only HTML/CSS/WordPress - suspicious
   - A "Mobile Developer" with no iOS/Android/Flutter/React Native
   - A "DevOps Engineer" with no CI/CD, Docker, Kubernetes, or cloud
   - A "Blockchain Developer" who lists only Excel and PowerPoint
   - A "Full Stack Developer" with no backend technologies

4. VERSION ANACHRONISMS: Does the candidate mention specific versions that don't match their claimed experience year?

5. EXPERIENCE DURATION FRAUD: Is the total claimed experience mathematically impossible given the dates?

6. TECHNOLOGY DEPTH vs BREADTH INCONSISTENCY: Do they claim expert-level mastery of 20+ technologies simultaneously? Real engineers specialise.

7. SKILL KEYWORD STUFFING: Are technologies mentioned with no context (no project, no outcome, no version)?

OUTPUT RULES: Output ONLY valid JSON. No markdown. No preamble. No explanation outside JSON.
{
  "authenticity_score": <integer 0-100, where 100=fully authentic, 0=clearly fabricated>,
  "risk_level": "<Low | Medium | High | Critical>",
  "verdict": "<Authentic | Likely Authentic | Suspicious | Likely Fabricated | Fabricated>",
  "timeline_issues": [
    {
      "technology": "<tech name>",
      "claimed_year": "<year or period candidate claims>",
      "actual_release": "<when this tech actually became available>",
      "severity": "<Minor | Major | Critical>",
      "detail": "<explanation>"
    }
  ],
  "compatibility_issues": [
    {
      "technologies": ["<tech1>", "<tech2>"],
      "issue": "<why these don't work together>",
      "severity": "<Minor | Major | Critical>",
      "detail": "<explanation>"
    }
  ],
  "role_mismatches": [
    {
      "role": "<claimed role>",
      "issue": "<what's missing or doesn't fit>",
      "severity": "<Minor | Major | Critical>",
      "detail": "<explanation>"
    }
  ],
  "positive_signals": ["<things that suggest authenticity>"],
  "red_flags": ["<summary of biggest concerns>"],
  "recommendation": "<one paragraph on whether to proceed, verify further, or reject due to authenticity concerns>",
  "interview_questions": ["<specific technical question to verify authenticity>", "<another question>", "<another>"]
}`;
};

// -- Authenticity Check API call -------------------------------
const callAuthenticityCheck = async (resumeText, jobRole) => {
  // Guard: if PROXY_URL is not configured, skip silently
  if (!PROXY_URL || PROXY_URL.includes("YOUR_VERCEL")) {
    console.warn("Lyca 360: PROXY_URL not configured - skipping authenticity check");
    return null;
  }
  try {
    const res = await fetch(PROXY_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        model: "claude-sonnet-4-20250514",
        max_tokens: 2000,
        messages: [{
          role: "user",
          content: buildAuthenticityPrompt(resumeText, jobRole)
        }],
      }),
    });
    if (!res.ok) throw new Error(`API error ${res.status}`);
    const data = await res.json();
    const text = data.content?.map(b => b.text || "").join("") || "";
    return JSON.parse(text.replace(/```json|```/g, "").trim());
  } catch (e) {
    console.error("Authenticity check failed:", e.message);
    throw e;
  }
};


const readAsArrayBuffer = (file) =>
  new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = () => rej(new Error("File read failed"));
    r.readAsArrayBuffer(file);
  });

const readAsBase64 = (file) =>
  new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result.split(",")[1]);
    r.onerror = () => rej(new Error("File read failed"));
    r.readAsDataURL(file);
  });

const extractFile = async (file) => {
  const ext = file.name.split(".").pop().toLowerCase();
  if (ext === "pdf") {
    const b64 = await readAsBase64(file);
    return { type: "pdf", b64, name: file.name };
  }
  if (ext === "docx") {
    const buf = await readAsArrayBuffer(file);
    const result = await mammoth.extractRawText({ arrayBuffer: buf });
    return { type: "text", text: result.value, name: file.name };
  }
  if (["odt", "odf"].includes(ext)) {
    const buf = await readAsArrayBuffer(file);
    const raw = new TextDecoder("utf-8", { fatal: false }).decode(new Uint8Array(buf));
    const matches = raw.match(/<text:[^>]*>([^<]*)<\/text:[^>]*>/g) || [];
    const text = matches.map(m => m.replace(/<[^>]+>/g, "")).filter(t => t.trim()).join("\n");
    return { type: "text", text: text || raw.replace(/<[^>]+>/g, " ").slice(0, 8000), name: file.name };
  }
  if (ext === "txt") return { type: "text", text: await file.text(), name: file.name };
  throw new Error(`Unsupported format: .${ext}`);
};

// -- API call via Vercel proxy ---------------------------------
const callAPI = async (fileData, pastedText, systemPrompt) => {
  if (!PROXY_URL || PROXY_URL.includes("YOUR_VERCEL")) {
    throw new Error("Proxy URL not configured. Please set your Vercel URL in App.js line 13.");
  }
  const userContent =
    fileData?.type === "pdf"
      ? [
          { type: "document", source: { type: "base64", media_type: "application/pdf", data: fileData.b64 } },
          { type: "text", text: "Evaluate this resume." },
        ]
      : `Evaluate this resume:\n\n${fileData?.text ?? pastedText}`;

  const res = await fetch(PROXY_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: "claude-sonnet-4-20250514",
      max_tokens: 1000,
      system: systemPrompt,
      messages: [{ role: "user", content: userContent }],
    }),
  });
  const data = await res.json();
  const text = data.content?.map(b => b.text || "").join("") || "";
  return JSON.parse(text.replace(/```json|```/g, "").trim());
};

// -- Google Search via Vercel proxy ----------------------------
const SEARCH_PROXY_URL = PROXY_URL.replace("/api/evaluate", "/api/search");

const callSearchAPI = async (name, context = "") => {
  const res = await fetch(SEARCH_PROXY_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, context }),
  });
  if (!res.ok) throw new Error("Search request failed");
  return await res.json();
};

// -- Web Intelligence Panel Component -------------------------
const SENTIMENT_COLOR = {
  "Positive":  "#00E5A0",
  "Neutral":   "#888",
  "Mixed":     "#FACC15",
  "Negative":  "#F87171",
  "Unknown":   "#555",
};
const PRESENCE_COLOR = {
  "Strong":   "#00E5A0",
  "Moderate": "#4ADE80",
  "Minimal":  "#FACC15",
  "None":     "#555",
  "Unknown":  "#555",
};
const RECOM_COLOR = {
  "Clear":                "#00E5A0",
  "Proceed with Caution": "#FACC15",
  "Investigate Further":  "#F87171",
};

const WebIntelPanel = ({ data, loading, error, onSearch, candidateName, compact = false }) => {
  if (loading) return (
    <div style={{ padding: "16px 18px", background: "#0a0a14", border: "1.5px solid #E5E7EB", borderRadius: 2, display: "flex", alignItems: "center", gap: 10 }}>
      <span style={{ animation: "pulseA 2s ease-in-out infinite", display: "inline-block", color: "#4A90E2" }}>◉</span>
      <span style={{ fontSize: 11, color: "#6B7280", letterSpacing: 1 }}>Searching Google for "{candidateName}"...</span>
    </div>
  );
  if (error) return (
    <div style={{ padding: "14px 18px", background: "#FEF2F2", border: "1px solid #F8717125", borderRadius: 2, fontSize: 11, color: "#F87171" }}>
      ✕ Search failed: {error} - Check your Google API key in Vercel.
    </div>
  );
  if (!data) return (
    <button onClick={onSearch} style={{ background: "#0a0a14", border: "1px dashed #2a2a5a", borderRadius: 2, padding: "14px 20px", cursor: "pointer", display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left", transition: "border-color 0.2s" }}
      onMouseOver={e => e.currentTarget.style.borderColor = "#4A90E2"}
      onMouseOut={e => e.currentTarget.style.borderColor = "#2a2a5a"}>
      <span style={{ fontSize: 16 }}>🌐</span>
      <div>
        <div style={{ fontSize: 11, color: "#4A90E2", letterSpacing: 1, marginBottom: 2 }}>Google Web Intelligence</div>
        <div style={{ fontSize: 10, color: "#9CA3AF", letterSpacing: 1 }}>Click to search "{candidateName}" across the web</div>
      </div>
      <span style={{ marginLeft: "auto", fontSize: 10, color: "#4A90E260", letterSpacing: 2, textTransform: "uppercase" }}>Search →</span>
    </button>
  );

  const { analysis, results, query } = data;
  const sentColor = SENTIMENT_COLOR[analysis.sentiment] || "#555";
  const presColor = PRESENCE_COLOR[analysis.online_presence] || "#555";
  const recColor = RECOM_COLOR[analysis.recommendation] || "#888";

  return (
    <div style={{ background: "#F9FAFB", border: "1.5px solid #E5E7EB", borderRadius: 2, overflow: "hidden" }}>
      {/* Header bar */}
      <div style={{ padding: "12px 16px", background: "#F3F4F6", borderBottom: "1px solid #E5E7EB", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <span style={{ fontSize: 14 }}>🌐</span>
        <div style={{ fontSize: 10, color: "#4A90E2", letterSpacing: 2, textTransform: "uppercase", fontFamily: "'DM Mono',monospace" }}>Web Intelligence</div>
        <div style={{ flex: 1 }} />
        {/* Pills */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ fontSize: 9, color: presColor, background: presColor + "15", border: `1px solid ${presColor}30`, padding: "3px 10px", borderRadius: 2, letterSpacing: 1, textTransform: "uppercase" }}>
            {analysis.online_presence} Presence
          </span>
          <span style={{ fontSize: 9, color: sentColor, background: sentColor + "15", border: `1px solid ${sentColor}30`, padding: "3px 10px", borderRadius: 2, letterSpacing: 1, textTransform: "uppercase" }}>
            {analysis.sentiment}
          </span>
          <span style={{ fontSize: 9, color: recColor, background: recColor + "15", border: `1px solid ${recColor}30`, padding: "3px 10px", borderRadius: 2, letterSpacing: 1, textTransform: "uppercase", fontWeight: 600 }}>
            {analysis.recommendation}
          </span>
        </div>
        <button onClick={onSearch} style={{ background: "none", border: "1px solid #2a2a4a", color: "#6B7280", cursor: "pointer", fontFamily: "'DM Mono',monospace", fontSize: 9, letterSpacing: 1, textTransform: "uppercase", padding: "4px 10px", borderRadius: 2 }}>↻ Re-search</button>
      </div>

      <div style={{ padding: "14px 16px" }}>
        {/* AI Summary */}
        <div style={{ fontSize: 12, color: "#374151", lineHeight: 1.7, marginBottom: 14 }}>{analysis.summary}</div>

        {/* Red flags + Positives + Platforms */}
        <div style={{ display: "grid", gridTemplateColumns: compact ? "1fr" : "1fr 1fr 1fr", gap: 10, marginBottom: 14 }}>
          {/* Platforms */}
          <div style={{ background: "#F9FAFB", border: "1.5px solid #E5E7EB", borderRadius: 2, padding: "12px 14px" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#000000", letterSpacing: 1, textTransform: "uppercase", marginBottom: 8 }}>Platforms Found</div>
            {(analysis.platforms_found || []).length > 0
              ? (analysis.platforms_found || []).map((p, i) => <div key={i} style={{ fontSize: 11, color: "#6B7280", marginBottom: 5 }}>● {p}</div>)
              : <div style={{ fontSize: 11, color: "#9CA3AF" }}>None detected</div>}
          </div>
          {/* Positive signals */}
          <div style={{ background: "#F0FDF4", border: "1px solid #00E5A020", borderRadius: 2, padding: "12px 14px" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#000000", letterSpacing: 1, textTransform: "uppercase", marginBottom: 8 }}>Positive Signals</div>
            {(analysis.positive_signals || []).length > 0
              ? (analysis.positive_signals || []).map((s, i) => <div key={i} style={{ fontSize: 11, color: "#4B5563", marginBottom: 5 }}>▸ {s}</div>)
              : <div style={{ fontSize: 11, color: "#9CA3AF" }}>None found</div>}
          </div>
          {/* Red flags */}
          <div style={{ background: "#FEF2F2", border: "1px solid #F8717120", borderRadius: 2, padding: "12px 14px" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#000000", letterSpacing: 1, textTransform: "uppercase", marginBottom: 8 }}>Red Flags</div>
            {(analysis.red_flags || []).length > 0
              ? (analysis.red_flags || []).map((f, i) => <div key={i} style={{ fontSize: 11, color: "#F87171", marginBottom: 5 }}>⚠ {f}</div>)
              : <div style={{ fontSize: 11, color: "#2a5a2a" }}>✓ None found</div>}
          </div>
        </div>

        {/* Raw search results */}
        {!compact && results?.length > 0 && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#000000", letterSpacing: 1, textTransform: "uppercase", marginBottom: 10 }}>
              Raw Google Results . <span style={{ color: "#9CA3AF" }}>"{query}"</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {results.slice(0, 6).map((r, i) => (
                <a key={i} href={r.link} target="_blank" rel="noopener noreferrer"
                  style={{ background: "#F9FAFB", border: "1.5px solid #E5E7EB", borderRadius: 2, padding: "10px 14px", display: "block", textDecoration: "none", transition: "border-color 0.2s" }}
                  onMouseOver={e => e.currentTarget.style.borderColor = "#2a2a5a"}
                  onMouseOut={e => e.currentTarget.style.borderColor = "#1a1a2e"}>
                  <div style={{ fontSize: 11, color: "#4A90E2", marginBottom: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.title}</div>
                  <div style={{ fontSize: 9, color: "#9CA3AF", marginBottom: 3, letterSpacing: 1 }}>{r.displayLink}</div>
                  <div style={{ fontSize: 10, color: "#666", lineHeight: 1.5, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{r.snippet}</div>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


// -- Authenticity Panel Component -----------------------------
const RISK_STYLE = {
  "Low":      { color: "#1A56DB", bg: "#00E5A010", border: "#BFDBFE", icon: "✓" },
  "Medium":   { color: "#FACC15", bg: "#FACC1510", border: "#FACC1530", icon: "⚠" },
  "High":     { color: "#F87171", bg: "#F8717110", border: "#F8717130", icon: "⚠" },
  "Critical": { color: "#EF4444", bg: "#EF444415", border: "#EF444440", icon: "🚨" },
};
const VERDICT_AUTH = {
  "Authentic":          { color: "#1A56DB", icon: "✓" },
  "Likely Authentic":   { color: "#4ADE80", icon: "✓" },
  "Suspicious":         { color: "#FACC15", icon: "⚠" },
  "Likely Fabricated":  { color: "#F87171", icon: "✕" },
  "Fabricated":         { color: "#EF4444", icon: "🚨" },
};
const SEV_COLOR = { Minor: "#FACC15", Major: "#F87171", Critical: "#EF4444" };

const AuthenticityPanel = ({ data, loading, error, onCheck, resumeAvailable }) => {
  const [expanded, setExpanded] = useState(null);

  // -- Early return states (hooks already called above) ---------
  if (!resumeAvailable) return (
    <div style={{ padding: "16px 18px", background: "#F9FAFB", border: "1px dashed #1e1e3a", borderRadius: 2, fontSize: 11, color: "#9CA3AF", textAlign: "center" }}>
      Paste a resume above to run the authenticity check.
    </div>
  );

  if (loading) return (
    <div style={{ padding: "20px 18px", background: "#F9FAFB", border: "1px solid #FACC1530", borderRadius: 2 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
        <span style={{ animation: "pulseA 1.5s ease-in-out infinite", display: "inline-block", color: "#FACC15", fontSize: 16 }}>◉</span>
        <div style={{ fontSize: 12, color: "#F97316", letterSpacing: 1 }}>Running forensic analysis...</div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {["Checking technology timelines...", "Verifying stack compatibility...", "Analysing role-tech alignment...", "Detecting keyword stuffing..."].map((s, i) => (
          <div key={i} style={{ fontSize: 10, color: "#9CA3AF", letterSpacing: 1, paddingLeft: 28 }}>→ {s}</div>
        ))}
      </div>
    </div>
  );

  if (error) return (
    <div style={{ padding: "14px 18px", background: "#FEF2F2", border: "1px solid #F8717125", borderRadius: 2, fontSize: 11, color: "#F87171" }}>
      ✕ Check failed: {error}
    </div>
  );

  if (!data) return (
    <button onClick={onCheck} style={{ background: "#F9FAFB", border: "1px dashed #FACC1540", borderRadius: 2, padding: "18px 20px", cursor: "pointer", display: "flex", alignItems: "center", gap: 14, width: "100%", textAlign: "left", transition: "border-color 0.2s" }}
      onMouseOver={e => e.currentTarget.style.borderColor = "#FACC1580"}
      onMouseOut={e => e.currentTarget.style.borderColor = "#FACC1540"}>
      <div style={{ width: 40, height: 40, borderRadius: "50%", background: "#FACC1510", border: "1px solid #FACC1530", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>🔬</div>
      <div>
        <div style={{ fontSize: 14, color: "#000000", fontWeight: 700, marginBottom: 4, fontFamily: "Calibri, sans-serif" }}>Resume Authenticity Check</div>
        <div style={{ fontSize: 10, color: "#6B7280", letterSpacing: 1, lineHeight: 1.5 }}>
          Checks technology timelines . stack compatibility . role alignment . fake resume detection
        </div>
      </div>
      <span style={{ marginLeft: "auto", fontSize: 10, color: "#FACC1560", letterSpacing: 2, textTransform: "uppercase", flexShrink: 0 }}>Run Check →</span>
    </button>
  );

  const rs = RISK_STYLE[data.risk_level] || RISK_STYLE["Medium"];
  const va = VERDICT_AUTH[data.verdict] || VERDICT_AUTH["Suspicious"];
  const totalIssues = (data.timeline_issues?.length || 0) + (data.compatibility_issues?.length || 0) + (data.role_mismatches?.length || 0);

  // Score arc
  const r = 32, circ = 2 * Math.PI * r, dash = (data.authenticity_score / 100) * circ;
  const sColor = data.authenticity_score >= 75 ? "#00E5A0" : data.authenticity_score >= 50 ? "#FACC15" : "#F87171";

  return (
    <div style={{ background: "#ffffff", border: `1px solid ${rs.border}`, borderTop: `3px solid ${rs.color}`, borderRadius: 2, overflow: "hidden" }}>
      {/* Header */}
      <div style={{ padding: "16px 20px", background: rs.bg, borderBottom: `1px solid ${rs.border}`, display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <svg width="76" height="76" viewBox="0 0 76 76" style={{ flexShrink: 0 }}>
            <circle cx="38" cy="38" r={r} fill="none" stroke="#1a1a2a" strokeWidth="6"/>
            <circle cx="38" cy="38" r={r} fill="none" stroke={sColor} strokeWidth="6"
              strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" transform="rotate(-90 38 38)"
              style={{ transition: "stroke-dasharray 1s ease" }}/>
            <text x="38" y="34" textAnchor="middle" fill={sColor} fontSize="16" fontWeight="700" fontFamily="'DM Mono',monospace">{data.authenticity_score}</text>
            <text x="38" y="48" textAnchor="middle" fill="#555" fontSize="8" fontFamily="'DM Mono',monospace">AUTH SCORE</text>
          </svg>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 14 }}>🔬</span>
              <span style={{ fontFamily: "Calibri, sans-serif", fontWeight: 700, fontSize: 15, color: "#000000" }}>Authenticity Check</span>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 11, color: va.color, fontWeight: 600 }}>{va.icon} {data.verdict}</span>
              <span style={{ fontSize: 9, color: rs.color, background: rs.bg, border: `1px solid ${rs.border}`, padding: "3px 10px", borderRadius: 2, letterSpacing: 1, textTransform: "uppercase" }}>{rs.icon} {data.risk_level} Risk</span>
              <span style={{ fontSize: 9, color: "#6B7280", letterSpacing: 1 }}>{totalIssues} issue{totalIssues !== 1 ? "s" : ""} found</span>
            </div>
          </div>
        </div>
        <button onClick={onCheck} style={{ marginLeft: "auto", background: "none", border: `1px solid ${rs.border}`, color: rs.color, cursor: "pointer", fontFamily: "'DM Mono',monospace", fontSize: 9, letterSpacing: 1, textTransform: "uppercase", padding: "5px 12px", borderRadius: 2 }}>↻ Re-check</button>
      </div>

      <div style={{ padding: "18px 20px" }}>
        {/* Recommendation */}
        <div style={{ fontSize: 12, color: "#374151", lineHeight: 1.8, marginBottom: 18, padding: "12px 16px", background: "#F9FAFB", borderRadius: 2, borderLeft: `3px solid ${rs.color}` }}>
          {data.recommendation}
        </div>

        {/* Red flags + positive signals */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 18 }}>
          <div style={{ background: "#FEF2F2", border: "1px solid #F8717120", borderRadius: 2, padding: "14px 16px" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#000000", letterSpacing: 1, textTransform: "uppercase", marginBottom: 10 }}>Red Flags</div>
            {(data.red_flags || []).length > 0
              ? (data.red_flags || []).map((f, i) => <div key={i} style={{ fontSize: 11, color: "#F87171", marginBottom: 7, lineHeight: 1.5 }}>⚠ {f}</div>)
              : <div style={{ fontSize: 11, color: "#2a5a2a" }}>✓ No major red flags</div>}
          </div>
          <div style={{ background: "#F0FDF4", border: "1px solid #00E5A020", borderRadius: 2, padding: "14px 16px" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#000000", letterSpacing: 1, textTransform: "uppercase", marginBottom: 10 }}>Positive Signals</div>
            {(data.positive_signals || []).length > 0
              ? (data.positive_signals || []).map((s, i) => <div key={i} style={{ fontSize: 11, color: "#4B5563", marginBottom: 7, lineHeight: 1.5 }}>▸ {s}</div>)
              : <div style={{ fontSize: 11, color: "#9CA3AF" }}>None identified</div>}
          </div>
        </div>

        {/* Timeline Issues */}
        {(data.timeline_issues || []).length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div onClick={() => setExpanded(expanded === "timeline" ? null : "timeline")}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", padding: "10px 14px", background: "#F9FAFB", border: "1px solid #F8717130", borderRadius: 2, marginBottom: expanded === "timeline" ? 0 : 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 14 }}>⏰</span>
                <span style={{ fontSize: 11, color: "#F87171", letterSpacing: 1, fontFamily: "Calibri, sans-serif", fontWeight: 700 }}>Timeline Fraud Checks</span>
                <span style={{ fontSize: 9, color: "#F87171", background: "#F8717115", border: "1px solid #F8717130", padding: "2px 8px", borderRadius: 10 }}>{data.timeline_issues.length}</span>
              </div>
              <span style={{ fontSize: 10, color: "#6B7280" }}>{expanded === "timeline" ? "▲" : "▼"}</span>
            </div>
            {expanded === "timeline" && (
              <div style={{ border: "1px solid #F8717120", borderTop: "none", borderRadius: "0 0 2px 2px", overflow: "hidden" }}>
                {data.timeline_issues.map((issue, i) => (
                  <div key={i} style={{ padding: "12px 16px", background: i % 2 === 0 ? "#0a0608" : "#08050a", borderBottom: i < data.timeline_issues.length - 1 ? "1px solid #1a0a0a" : "none" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                      <span style={{ fontSize: 10, color: SEV_COLOR[issue.severity] || "#FACC15", background: (SEV_COLOR[issue.severity] || "#FACC15") + "15", border: `1px solid ${(SEV_COLOR[issue.severity] || "#FACC15")}30`, padding: "2px 8px", borderRadius: 2, letterSpacing: 1, textTransform: "uppercase" }}>{issue.severity}</span>
                      <span style={{ fontSize: 12, color: "#111827", fontFamily: "Calibri, sans-serif", fontWeight: 700 }}>{issue.technology}</span>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 6 }}>
                      <div style={{ fontSize: 10, color: "#6B7280" }}>Claimed: <span style={{ color: "#F87171" }}>{issue.claimed_year}</span></div>
                      <div style={{ fontSize: 10, color: "#6B7280" }}>Actually released: <span style={{ color: "#1A56DB" }}>{issue.actual_release}</span></div>
                    </div>
                    <div style={{ fontSize: 11, color: "#6B7280", lineHeight: 1.6 }}>{issue.detail}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Compatibility Issues */}
        {(data.compatibility_issues || []).length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div onClick={() => setExpanded(expanded === "compat" ? null : "compat")}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", padding: "10px 14px", background: "#F9FAFB", border: "1px solid #F8717130", borderRadius: 2 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 14 }}>⚡</span>
                <span style={{ fontSize: 11, color: "#F87171", letterSpacing: 1, fontFamily: "Calibri, sans-serif", fontWeight: 700 }}>Technology Compatibility Issues</span>
                <span style={{ fontSize: 9, color: "#F87171", background: "#F8717115", border: "1px solid #F8717130", padding: "2px 8px", borderRadius: 10 }}>{data.compatibility_issues.length}</span>
              </div>
              <span style={{ fontSize: 10, color: "#6B7280" }}>{expanded === "compat" ? "▲" : "▼"}</span>
            </div>
            {expanded === "compat" && (
              <div style={{ border: "1px solid #F8717120", borderTop: "none", borderRadius: "0 0 2px 2px", overflow: "hidden" }}>
                {data.compatibility_issues.map((issue, i) => (
                  <div key={i} style={{ padding: "12px 16px", background: i % 2 === 0 ? "#0a0608" : "#08050a", borderBottom: i < data.compatibility_issues.length - 1 ? "1px solid #1a0a0a" : "none" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 10, color: SEV_COLOR[issue.severity] || "#FACC15", background: (SEV_COLOR[issue.severity] || "#FACC15") + "15", border: `1px solid ${(SEV_COLOR[issue.severity] || "#FACC15")}30`, padding: "2px 8px", borderRadius: 2, letterSpacing: 1, textTransform: "uppercase" }}>{issue.severity}</span>
                      {(issue.technologies || []).map((t, j) => (
                        <span key={j} style={{ fontSize: 11, color: "#111827", background: "#1a1a2a", border: "1px solid #2a2a4a", padding: "3px 10px", borderRadius: 2, fontFamily: "'DM Mono',monospace" }}>{t}</span>
                      ))}
                      <span style={{ fontSize: 11, color: "#6B7280" }}>- {issue.issue}</span>
                    </div>
                    <div style={{ fontSize: 11, color: "#6B7280", lineHeight: 1.6 }}>{issue.detail}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Role Mismatches */}
        {(data.role_mismatches || []).length > 0 && (
          <div style={{ marginBottom: 18 }}>
            <div onClick={() => setExpanded(expanded === "role" ? null : "role")}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", padding: "10px 14px", background: "#F9FAFB", border: "1px solid #FACC1530", borderRadius: 2 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 14 }}>👤</span>
                <span style={{ fontSize: 11, color: "#F97316", letterSpacing: 1, fontFamily: "Calibri, sans-serif", fontWeight: 700 }}>Role-Technology Mismatches</span>
                <span style={{ fontSize: 9, color: "#FACC15", background: "#FACC1515", border: "1px solid #FACC1530", padding: "2px 8px", borderRadius: 10 }}>{data.role_mismatches.length}</span>
              </div>
              <span style={{ fontSize: 10, color: "#6B7280" }}>{expanded === "role" ? "▲" : "▼"}</span>
            </div>
            {expanded === "role" && (
              <div style={{ border: "1px solid #FACC1520", borderTop: "none", borderRadius: "0 0 2px 2px", overflow: "hidden" }}>
                {data.role_mismatches.map((issue, i) => (
                  <div key={i} style={{ padding: "12px 16px", background: i % 2 === 0 ? "#0a0a08" : "#080808", borderBottom: i < data.role_mismatches.length - 1 ? "1px solid #1a1a10" : "none" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                      <span style={{ fontSize: 10, color: SEV_COLOR[issue.severity] || "#FACC15", background: (SEV_COLOR[issue.severity] || "#FACC15") + "15", border: `1px solid ${(SEV_COLOR[issue.severity] || "#FACC15")}30`, padding: "2px 8px", borderRadius: 2, letterSpacing: 1, textTransform: "uppercase" }}>{issue.severity}</span>
                      <span style={{ fontSize: 12, color: "#FACC15", fontFamily: "Calibri, sans-serif", fontWeight: 700 }}>{issue.role}</span>
                    </div>
                    <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 5 }}>{issue.issue}</div>
                    <div style={{ fontSize: 11, color: "#666", lineHeight: 1.6 }}>{issue.detail}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Interview questions */}
        {(data.interview_questions || []).length > 0 && (
          <div style={{ background: "#F3F4F6", border: "1px solid #4A90E220", borderRadius: 2, padding: "16px 18px" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#000000", letterSpacing: 1, textTransform: "uppercase", marginBottom: 12 }}>
              🎯 Suggested Verification Questions
            </div>
            <div style={{ fontSize: 10, color: "#6B7280", letterSpacing: 1, marginBottom: 10 }}>Ask these in interview to verify if the candidate truly knows what they claim:</div>
            {data.interview_questions.map((q, i) => (
              <div key={i} style={{ padding: "8px 12px", background: "#F9FAFB", border: "1.5px solid #E5E7EB", borderRadius: 2, marginBottom: 6, fontSize: 11, color: "#374151", lineHeight: 1.6 }}>
                <span style={{ color: "#4A90E2", marginRight: 8, fontWeight: 700 }}>Q{i+1}.</span>{q}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// -- Constants -------------------------------------------------
const VERDICTS = ["Strong Yes", "Yes", "Maybe", "No", "Strong No"];
const VS = {
  "Strong Yes": { accent: "#00E5A0", dim: "#00E5A015" },
  "Yes":        { accent: "#4ADE80", dim: "#4ADE8015" },
  "Maybe":      { accent: "#FACC15", dim: "#FACC1515" },
  "No":         { accent: "#F87171", dim: "#F8717115" },
  "Strong No":  { accent: "#EF4444", dim: "#EF444415" },
};
const scoreColor = s => s >= 80 ? "#00E5A0" : s >= 60 ? "#FACC15" : "#F87171";

// -- SmallScore ------------------------------------------------
const SmallScore = ({ score }) => {
  const r = 18, circ = 2 * Math.PI * r, dash = (score / 100) * circ;
  return (
    <svg width="44" height="44" viewBox="0 0 44 44">
      <circle cx="22" cy="22" r={r} fill="none" stroke="#1a1a2a" strokeWidth="4" />
      <circle cx="22" cy="22" r={r} fill="none" stroke={scoreColor(score)} strokeWidth="4"
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
        transform="rotate(-90 22 22)" style={{ transition: "stroke-dasharray 0.8s ease" }} />
      <text x="22" y="26" textAnchor="middle" fill={scoreColor(score)} fontSize="11" fontWeight="700" fontFamily="'DM Mono',monospace">{score}</text>
    </svg>
  );
};

// -------------------------------------------------------------
// LOGIN SCREEN
// -------------------------------------------------------------
const LoginScreen = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSignUp, setIsSignUp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [loginType, setLoginType] = useState(null);

  const handleSubmit = async () => {
    if (!email || !password) { setErr("Please enter email and password."); return; }
    setLoading(true); setErr(""); setMsg("");
    try {
      if (isSignUp) {
        const { error } = await supabase.auth.signUp({ email, password });
        if (error) throw error;
        setMsg("Account created! Check your email to confirm, then log in.");
      } else {
        const { data, error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        onLogin(data.user);
      }
    } catch (e) { setErr(e.message); }
    setLoading(false);
  };

  if (!loginType) {
    return (
      <div style={{ minHeight: "100vh", background: "#ffffff", fontFamily: "Calibri, sans-serif" }}>
        <div style={{ padding: "20px 32px", borderBottom: "2px solid #BFDBFE" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ background: "#1A56DB", borderRadius: 8, width: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ color: "#ffffff", fontWeight: 700, fontSize: 15 }}>L</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", width: 108 }}>
              <div style={{ fontFamily: "Calibri, sans-serif", fontWeight: 700, fontSize: 24, lineHeight: 1 }}>
                <span style={{ color: "#F97316" }}>LYCA</span>
                <span style={{ color: "#1A56DB" }}>360</span>
              </div>
              <div style={{ fontSize: 8, color: "#000000", fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", textAlign: "justify" }}>Staffing Intelligence</div>
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "calc(100vh - 80px)", padding: "20px" }}>
          <div style={{ textAlign: "center", maxWidth: 560, width: "100%" }}>
            <h1 style={{ fontFamily: "Calibri, sans-serif", fontSize: 28, fontWeight: 700, color: "#000000", marginBottom: 36 }}>Welcome to Lyca 360</h1>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 20 }}>
              <div onClick={() => setLoginType("admin")} style={{ background: "#ffffff", border: "2px solid #1A56DB", borderRadius: 12, padding: "30px 22px", cursor: "pointer" }}>
                <div style={{ width: 58, height: 58, background: "#1A56DB", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                  <span style={{ fontSize: 24 }}>&#9881;</span>
                </div>
                <h2 style={{ fontFamily: "Calibri, sans-serif", fontSize: 20, fontWeight: 700, color: "#000000", marginBottom: 16 }}>Admin</h2>
                <div style={{ background: "#1A56DB", color: "#ffffff", padding: "10px 20px", borderRadius: 8, fontFamily: "Calibri, sans-serif", fontWeight: 700, fontSize: 14 }}>Login as Admin</div>
              </div>
              <div onClick={() => setLoginType("recruiter")} style={{ background: "#ffffff", border: "2px solid #F97316", borderRadius: 12, padding: "30px 22px", cursor: "pointer" }}>
                <div style={{ width: 58, height: 58, background: "#F97316", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                  <span style={{ fontSize: 24 }}>&#128100;</span>
                </div>
                <h2 style={{ fontFamily: "Calibri, sans-serif", fontSize: 20, fontWeight: 700, color: "#000000", marginBottom: 16 }}>Recruiter</h2>
                <div style={{ background: "#F97316", color: "#ffffff", padding: "10px 20px", borderRadius: 8, fontFamily: "Calibri, sans-serif", fontWeight: 700, fontSize: 14 }}>Login as Recruiter</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isAdminType = loginType === "admin";
  const accentColor = isAdminType ? "#1A56DB" : "#F97316";

  return (
    <div style={{ minHeight: "100vh", background: "#ffffff", fontFamily: "Calibri, sans-serif" }}>
      <div style={{ padding: "20px 32px", borderBottom: "2px solid #BFDBFE" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ background: "#1A56DB", borderRadius: 8, width: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ color: "#ffffff", fontWeight: 700, fontSize: 15 }}>L</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", width: 108 }}>
            <div style={{ fontFamily: "Calibri, sans-serif", fontWeight: 700, fontSize: 24, lineHeight: 1 }}>
              <span style={{ color: "#F97316" }}>LYCA</span>
              <span style={{ color: "#1A56DB" }}>360</span>
            </div>
            <div style={{ fontSize: 8, color: "#000000", fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", textAlign: "justify" }}>Staffing Intelligence</div>
          </div>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "calc(100vh - 80px)", padding: "20px" }}>
        <div style={{ width: "100%", maxWidth: 420, border: "2px solid " + accentColor, borderRadius: 12, padding: "30px 26px" }}>
          <button onClick={() => { setLoginType(null); setErr(""); setMsg(""); }} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#6B7280", marginBottom: 20, padding: 0, fontFamily: "Calibri, sans-serif" }}>Back</button>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 26 }}>
            <div style={{ width: 42, height: 42, background: accentColor, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>
              <span>{isAdminType ? "A" : "R"}</span>
            </div>
            <div>
              <div style={{ fontFamily: "Calibri, sans-serif", fontSize: 21, fontWeight: 700, color: "#000000" }}>{isAdminType ? "Admin Login" : "Recruiter Login"}</div>
              <div style={{ fontFamily: "Calibri, sans-serif", fontSize: 13, color: "#6B7280" }}>Sign in to your account</div>
            </div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontFamily: "Calibri, sans-serif", fontSize: 14, fontWeight: 700, color: "#000000", display: "block", marginBottom: 6 }}>Email Address</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" onKeyDown={e => e.key === "Enter" && handleSubmit()} style={{ border: "1.5px solid #D1D5DB", background: "#F9FAFB", color: "#111827", fontFamily: "Calibri, sans-serif", fontSize: 15, padding: "11px 14px", borderRadius: 8, width: "100%", outline: "none", boxSizing: "border-box" }} />
          </div>
          <div style={{ marginBottom: 22 }}>
            <label style={{ fontFamily: "Calibri, sans-serif", fontSize: 14, fontWeight: 700, color: "#000000", display: "block", marginBottom: 6 }}>Password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter your password" onKeyDown={e => e.key === "Enter" && handleSubmit()} style={{ border: "1.5px solid #D1D5DB", background: "#F9FAFB", color: "#111827", fontFamily: "Calibri, sans-serif", fontSize: 15, padding: "11px 14px", borderRadius: 8, width: "100%", outline: "none", boxSizing: "border-box" }} />
          </div>
          {err && <div style={{ background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: 6, padding: "10px 14px", color: "#DC2626", fontFamily: "Calibri, sans-serif", fontSize: 14, marginBottom: 14 }}>{err}</div>}
          {msg && <div style={{ background: "#F0FDF4", border: "1px solid #86EFAC", borderRadius: 6, padding: "10px 14px", color: "#16A34A", fontFamily: "Calibri, sans-serif", fontSize: 14, marginBottom: 14 }}>{msg}</div>}
          <button onClick={handleSubmit} disabled={loading} style={{ background: accentColor, color: "#ffffff", border: "none", cursor: loading ? "not-allowed" : "pointer", fontFamily: "Calibri, sans-serif", fontWeight: 700, fontSize: 15, padding: "13px", borderRadius: 8, width: "100%", opacity: loading ? 0.7 : 1 }}>
            {loading ? "Please wait..." : (isSignUp ? "Create Account" : "Sign In as " + (isAdminType ? "Admin" : "Recruiter"))}
          </button>
          <div style={{ textAlign: "center", marginTop: 16 }}>
            <button onClick={() => { setIsSignUp(!isSignUp); setErr(""); setMsg(""); }} style={{ background: "none", border: "none", color: accentColor, cursor: "pointer", fontFamily: "Calibri, sans-serif", fontSize: 14, textDecoration: "underline" }}>
              {isSignUp ? "Already have an account? Sign in" : "No account? Create one"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// -------------------------------------------------------------
// MAIN APP
// -------------------------------------------------------------
export default function Lyca360() {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null); // "admin" | "recruiter"
  const [sessionLoading, setSessionLoading] = useState(true);
  const [tab, setTab] = useState("evaluate");
  const [examples, setExamples] = useState([]);
  const [examplesLoading, setExamplesLoading] = useState(false);
  const [authEnabled, setAuthEnabled] = useState(() => {
    try { return localStorage.getItem("lyca360-auth-enabled") !== "false"; } catch { return true; }
  });
  const toggleAuth = (val) => {
    setAuthEnabled(val);
    try { localStorage.setItem("lyca360-auth-enabled", String(val)); } catch {}
  };
  const isAdmin = userRole === "admin";

  // -- Auth check on mount -----------------------------------
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setSessionLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  // -- Load user role from Supabase --------------------------
  useEffect(() => {
    if (!user) { setUserRole(null); return; }
    supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", user.id)
      .single()
      .then(({ data }) => {
        setUserRole(data?.role || "recruiter");
      })
      .catch(() => {
        setUserRole("recruiter");
      });
  }, [user]);

  // -- Load training data from Supabase ---------------------
  useEffect(() => {
    if (!user) return;
    setExamplesLoading(true);
    supabase
      .from("training_data")
      .select("*")
      .order("created_at", { ascending: false })
      .then(({ data, error }) => {
        if (!error && data) setExamples(data);
        setExamplesLoading(false);
      })
      .catch(e => {
        console.warn("Training data load failed:", e.message);
        setExamplesLoading(false);
      });
  }, [user]);

  // -- Save training example to Supabase --------------------
  const saveTrainingExample = async (example) => {
    const { data, error } = await supabase
      .from("training_data")
      .insert([{ ...example, user_id: user.id }])
      .select()
      .single();
    if (!error && data) setExamples(prev => [data, ...prev]);
    return !error;
  };

  // -- Delete training example -------------------------------
  const deleteTrainingExample = async (id) => {
    const { error } = await supabase.from("training_data").delete().eq("id", id);
    if (!error) setExamples(prev => prev.filter(e => e.id !== id));
  };

  // -- Save evaluation to history ----------------------------
  const saveEvaluation = async (candidate, result, jobRole, batchId) => {
    await supabase.from("evaluations").insert([{
      user_id: user.id,
      candidate_name: candidate.name || "Unknown",
      job_role: jobRole,
      resume_text: candidate.pastedText || candidate.fileData?.text || "",
      score: result.score,
      verdict: result.verdict,
      summary: result.summary,
      strengths: result.strengths,
      concerns: result.concerns,
      recommendation: result.recommendation,
      standout: result.standout,
      confidence: result.confidence,
      batch_id: batchId,
    }]);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setExamples([]);
  };

  // -- Batch evaluation state --------------------------------
  const MAX_SLOTS = 10, MIN_SLOTS = 1;
  const emptySlot = () => ({ id: Date.now() + Math.random(), name: "", mode: "paste", pastedText: "", file: null, fileData: null, fileLoading: false, fileErr: "", result: null, status: "idle", webData: null, webLoading: false, webError: null, authData: null, authLoading: false, authError: null });
  const [slots, setSlots] = useState(() => Array.from({ length: MIN_SLOTS }, emptySlot));
  const [jobRole, setJobRole] = useState("");
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchDone, setBatchDone] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [batchErr, setBatchErr] = useState("");
  const resultRef = useRef();

  const updateSlot = (id, patch) => setSlots(prev => prev.map(s => s.id === id ? { ...s, ...patch } : s));
  const addSlot = () => { if (slots.length < MAX_SLOTS) setSlots(prev => [...prev, emptySlot()]); };
  const removeSlot = (id) => { if (slots.length > MIN_SLOTS) setSlots(prev => prev.filter(s => s.id !== id)); };

  const handleSlotFile = async (id, f) => {
    updateSlot(id, { file: f, fileErr: "", fileData: null, fileLoading: true });
    try {
      const d = await extractFile(f);
      updateSlot(id, { fileData: d, fileLoading: false, name: f.name.replace(/\.[^.]+$/, "") });
    } catch (e) {
      updateSlot(id, { fileErr: e.message, file: null, fileLoading: false });
    }
  };

  const handleBatchEvaluate = async () => {
    const ready = slots.filter(s => s.mode === "paste" ? s.pastedText.trim() : s.fileData);
    if (ready.length < MIN_SLOTS) { setBatchErr(`Please add at least 1 resume to evaluate.`); return; }
    setBatchErr(""); setBatchLoading(true); setBatchDone(false);
    setSlots(prev => prev.map(s => ({ ...s, result: null, status: "idle", authData: null, authLoading: false })));
    const sys = buildSystemPrompt(examples, jobRole);
    const batchId = crypto.randomUUID();

    await Promise.all(slots.map(async (s) => {
      const hasContent = s.mode === "paste" ? s.pastedText.trim() : s.fileData;
      if (!hasContent) return;

      const resumeText = s.mode === "paste" ? s.pastedText : (s.fileData?.text || "");
      updateSlot(s.id, { status: "loading", authLoading: true });

      // Run AI evaluation AND authenticity check simultaneously
      const [evalResult, authResult] = await Promise.allSettled([
        callAPI(s.mode === "upload" ? s.fileData : null, s.pastedText, sys),
        authEnabled ? callAuthenticityCheck(resumeText, jobRole) : Promise.resolve(null),
      ]);

      const evalData  = evalResult.status  === "fulfilled" ? evalResult.value  : null;
      const authData  = authResult.status  === "fulfilled" ? authResult.value  : null;

      updateSlot(s.id, {
        result:      evalData,
        authData:    authData,
        status:      evalData ? "done" : "error",
        authLoading: false,
      });

      if (evalData) await saveEvaluation(s, evalData, jobRole, batchId);
    }));

    setBatchLoading(false); setBatchDone(true);
    setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
  };

  const rankedSlots = [...slots].filter(s => s.result).sort((a, b) => (b.result?.score || 0) - (a.result?.score || 0));

  // -- Batch slot auth check ---------------------------------
  const handleSlotAuthCheck = async (slot) => {
    const text = slot.mode === "paste" ? slot.pastedText : slot.fileData?.text;
    if (!text?.trim()) return;
    updateSlot(slot.id, { authLoading: true, authError: null });
    try {
      const result = await callAuthenticityCheck(text, jobRole);
      updateSlot(slot.id, { authData: result, authLoading: false });
    } catch (e) {
      updateSlot(slot.id, { authLoading: false, authError: e.message });
    }
  };

  // -- Batch web search --------------------------------------
  const handleSlotWebSearch = async (slot) => {
    if (!slot.name.trim()) return;
    updateSlot(slot.id, { webLoading: true, webError: null });
    try {
      const data = await callSearchAPI(slot.name, jobRole);
      updateSlot(slot.id, { webData: data, webLoading: false });
    } catch (e) {
      updateSlot(slot.id, { webLoading: false, webError: e.message });
    }
  };

  // -- Train state -------------------------------------------
  const [tMode, setTMode] = useState("paste");
  const [tPasted, setTPasted] = useState("");
  const [tFile, setTFile] = useState(null);
  const [tFileData, setTFileData] = useState(null);
  const [tFileErr, setTFileErr] = useState("");
  const [tFeedback, setTFeedback] = useState("");
  const [tScore, setTScore] = useState(70);
  const [tVerdict, setTVerdict] = useState("Yes");
  const [trainSaving, setTrainSaving] = useState(false);
  const [trainSaved, setTrainSaved] = useState(false);
  const [err, setErr] = useState("");
  const [expanded, setExpanded] = useState(null);
  // Train Engine web search
  const [tCandidateName, setTCandidateName] = useState("");
  const [tWebData, setTWebData] = useState(null);
  const [tWebLoading, setTWebLoading] = useState(false);
  const [tWebError, setTWebError] = useState(null);
  // Train authenticity check state
  const [tAuthData, setTAuthData] = useState(null);
  const [tAuthLoading, setTAuthLoading] = useState(false);
  const [tAuthError, setTAuthError] = useState(null);

  const handleTrainAuthCheck = async () => {
    const text = tMode === "upload" ? tFileData?.text : tPasted;
    if (!text?.trim()) { setErr("Paste or upload a resume first."); return; }
    setTAuthLoading(true); setTAuthError(null); setTAuthData(null);
    try {
      const result = await callAuthenticityCheck(text, tFeedback || "");
      setTAuthData(result);
    } catch (e) { setTAuthError(e.message); }
    setTAuthLoading(false);
  };

  const handleTrainWebSearch = async () => {
    if (!tCandidateName.trim()) return;
    setTWebLoading(true); setTWebError(null); setTWebData(null);
    try {
      const data = await callSearchAPI(tCandidateName, "");
      setTWebData(data);
    } catch (e) { setTWebError(e.message); }
    setTWebLoading(false);
  };

  // -- History state -----------------------------------------
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const { data } = await supabase
        .from("evaluations")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(50);
      if (data) setHistory(data);
    } catch (e) {
      console.warn("History load failed:", e.message);
    }
    setHistoryLoading(false);
  };

  useEffect(() => { if (tab === "history" && user) loadHistory(); }, [tab, user]);

  // -- Admin Panel state -------------------------------------
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminLoading, setAdminLoading] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserPassword, setNewUserPassword] = useState("");
  const [newUserRole, setNewUserRole] = useState("recruiter");
  const [adminMsg, setAdminMsg] = useState("");
  const [adminErr, setAdminErr] = useState("");
  const [creatingUser, setCreatingUser] = useState(false);

  const loadAdminUsers = async () => {
    setAdminLoading(true);
    try {
      const { data } = await supabase
        .from("user_roles")
        .select("*")
        .order("created_at", { ascending: false });
      if (data) setAdminUsers(data);
    } catch (e) {
      console.warn("Admin users load failed:", e.message);
    }
    setAdminLoading(false);
  };

  useEffect(() => { if (tab === "admin" && isAdmin) loadAdminUsers(); }, [tab, isAdmin]);

  const handleCreateUser = async () => {
    if (!newUserEmail.trim() || !newUserPassword.trim()) {
      setAdminErr("Please enter both email and password."); return;
    }
    setCreatingUser(true); setAdminErr(""); setAdminMsg("");
    try {
      // Sign up the new user via Supabase Auth
      const { data: signupData, error: signupError } = await supabase.auth.signUp({
        email: newUserEmail.trim(),
        password: newUserPassword.trim(),
        options: { emailRedirectTo: window.location.origin },
      });
      if (signupError) throw signupError;
      // Save their role
      if (signupData?.user) {
        await supabase.from("user_roles").insert([{
          user_id: signupData.user.id,
          email: newUserEmail.trim(),
          role: newUserRole,
          created_by: user.id,
        }]);
      }
      setAdminMsg(`✓ Account created for ${newUserEmail}. They will receive a confirmation email.`);
      setNewUserEmail(""); setNewUserPassword(""); setNewUserRole("recruiter");
      loadAdminUsers();
    } catch (e) {
      setAdminErr(e.message);
    }
    setCreatingUser(false);
  };

  const handleChangeRole = async (userId, newRole) => {
    await supabase.from("user_roles").update({ role: newRole }).eq("user_id", userId);
    setAdminUsers(prev => prev.map(u => u.user_id === userId ? { ...u, role: newRole } : u));
  };

  const handleRemoveUser = async (userId, email) => {
    if (!window.confirm(`Remove access for ${email}? They will no longer be able to log in.`)) return;
    await supabase.from("user_roles").delete().eq("user_id", userId);
    setAdminUsers(prev => prev.filter(u => u.user_id !== userId));
  };

  // -- Candidate Database state ------------------------------
  const [candidates, setCandidates] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [showAddCandidate, setShowAddCandidate] = useState(false);
  const [expandedCandidate, setExpandedCandidate] = useState(null);
  const [validatingId, setValidatingId] = useState(null);
  const [candidateSearch, setCandidateSearch] = useState("");
  const [candidateFilter, setCandidateFilter] = useState("all");
  const [searchMode, setSearchMode] = useState("simple"); // "simple" | "boolean"
  const [boolQuery, setBoolQuery] = useState({ must: "", should: "", mustNot: "", minScore: 0, maxScore: 100, minExp: "", maxExp: "", location: "", role: "" });
  const [sortBy, setSortBy] = useState("score"); // "score" | "auth" | "name" | "date" | "exp"
  const [sortDir, setSortDir] = useState("desc"); // "asc" | "desc"
  const [newCandidate, setNewCandidate] = useState({
    full_name: "", email: "", phone: "", location: "",
    current_title: "", experience_years: "", skills: "",
    education: "", linkedin: "", resume_text: "", applied_role: "", notes: ""
  });
  const [savingCandidate, setSavingCandidate] = useState(false);

  // -- Web Intelligence state (per candidate) ----------------
  const [webData, setWebData] = useState({});
  const [webLoading, setWebLoading] = useState({});
  const [webError, setWebError] = useState({});
  // -- Authenticity state (per candidate) -------------------
  const [authData, setAuthData] = useState({});
  const [authLoading, setAuthLoading] = useState({});
  const [authError, setAuthError] = useState({});

  // -- Web search for a candidate ----------------------------
  const handleWebSearch = async (candidate) => {
    const id = candidate.id;
    setWebLoading(p => ({ ...p, [id]: true }));
    setWebError(p => ({ ...p, [id]: null }));
    try {
      const data = await callSearchAPI(candidate.full_name, candidate.applied_role || candidate.current_title || "");
      setWebData(p => ({ ...p, [id]: data }));
      // Persist to Supabase
      await supabase.from("candidates").update({
        web_searched: true,
        web_searched_at: new Date().toISOString(),
        web_query: data.query,
        web_results: data.results,
        web_online_presence: data.analysis.online_presence,
        web_sentiment: data.analysis.sentiment,
        web_red_flags: data.analysis.red_flags,
        web_positive_signals: data.analysis.positive_signals,
        web_platforms: data.analysis.platforms_found,
        web_summary: data.analysis.summary,
        web_recommendation: data.analysis.recommendation,
      }).eq("id", id);
      setCandidates(prev => prev.map(c => c.id === id ? {
        ...c, web_searched: true, web_summary: data.analysis.summary,
        web_sentiment: data.analysis.sentiment, web_online_presence: data.analysis.online_presence,
        web_recommendation: data.analysis.recommendation, web_red_flags: data.analysis.red_flags,
      } : c));
    } catch (e) {
      setWebError(p => ({ ...p, [id]: e.message }));
    }
    setWebLoading(p => ({ ...p, [id]: false }));
  };

  const loadCandidates = async () => {
    setCandidatesLoading(true);
    try {
      const { data } = await supabase
        .from("candidates")
        .select("*")
        .order("created_at", { ascending: false });
      if (data) setCandidates(data);
    } catch (e) {
      console.warn("Candidates load failed:", e.message);
    }
    setCandidatesLoading(false);
  };

  useEffect(() => { if (tab === "candidates" && user) loadCandidates(); }, [tab, user]);

  const handleAddCandidate = async () => {
    if (!newCandidate.full_name.trim()) return;
    setSavingCandidate(true);
    const { data, error } = await supabase
      .from("candidates")
      .insert([{ ...newCandidate, user_id: user.id, ai_score: null, ai_verdict: null, ai_validated: false }])
      .select().single();
    if (!error && data) {
      setCandidates(prev => [data, ...prev]);
      setNewCandidate({ full_name: "", email: "", phone: "", location: "", current_title: "", experience_years: "", skills: "", education: "", linkedin: "", resume_text: "", applied_role: "", notes: "" });
      setShowAddCandidate(false);
    }
    setSavingCandidate(false);
  };

  const handleDeleteCandidate = async (id) => {
    if (!window.confirm("Delete this candidate?")) return;
    await supabase.from("candidates").delete().eq("id", id);
    setCandidates(prev => prev.filter(c => c.id !== id));
    if (expandedCandidate === id) setExpandedCandidate(null);
  };

  const handleValidateCandidate = async (candidate) => {
    setValidatingId(candidate.id);
    // Clear any existing auth result so UI shows fresh loading
    setAuthData(p => ({ ...p, [candidate.id]: null }));
    setAuthLoading(p => ({ ...p, [candidate.id]: true }));

    const resumeContent = candidate.resume_text ||
      `Name: ${candidate.full_name}
Title: ${candidate.current_title}
Experience: ${candidate.experience_years} years
Skills: ${candidate.skills}
Education: ${candidate.education}
Location: ${candidate.location}
Applied Role: ${candidate.applied_role}
Notes: ${candidate.notes}`;

    try {
      const sys = buildSystemPrompt(examples, candidate.applied_role || "");

      // Run AI evaluation AND authenticity check simultaneously
      const [evalResult, authResult] = await Promise.allSettled([
        callAPI(null, resumeContent, sys),
        authEnabled ? callAuthenticityCheck(resumeContent, candidate.applied_role || "") : Promise.resolve(null),
      ]);

      const result = evalResult.status === "fulfilled" ? evalResult.value : null;
      const auth   = authResult.status  === "fulfilled" ? authResult.value  : null;

      // Save evaluation result
      if (result) {
        await supabase.from("candidates").update({
          ai_score: result.score,
          ai_verdict: result.verdict,
          ai_summary: result.summary,
          ai_strengths: result.strengths,
          ai_concerns: result.concerns,
          ai_recommendation: result.recommendation,
          ai_standout: result.standout,
          ai_confidence: result.confidence,
          ai_validated: true,
          ai_validated_at: new Date().toISOString(),
          // Also store authenticity in DB
          ...(auth ? {
            auth_score: auth.authenticity_score,
            auth_risk: auth.risk_level,
            auth_verdict: auth.verdict,
            auth_red_flags: auth.red_flags,
            auth_timeline_issues: auth.timeline_issues,
            auth_compatibility_issues: auth.compatibility_issues,
            auth_role_mismatches: auth.role_mismatches,
            auth_positive_signals: auth.positive_signals,
            auth_recommendation: auth.recommendation,
            auth_interview_questions: auth.interview_questions,
            auth_checked_at: new Date().toISOString(),
          } : {}),
        }).eq("id", candidate.id);

        setCandidates(prev => prev.map(c => c.id === candidate.id ? {
          ...c,
          ai_score: result.score, ai_verdict: result.verdict,
          ai_summary: result.summary, ai_strengths: result.strengths,
          ai_concerns: result.concerns, ai_recommendation: result.recommendation,
          ai_standout: result.standout, ai_confidence: result.confidence,
          ai_validated: true, ai_validated_at: new Date().toISOString(),
        } : c));
      }

      // Store auth result in state
      if (auth) setAuthData(p => ({ ...p, [candidate.id]: auth }));

    } catch (e) { console.error(e); }

    setValidatingId(null);
    setAuthLoading(p => ({ ...p, [candidate.id]: false }));
  };

  // -- Authenticity check for a candidate -------------------
  const handleAuthCheck = async (candidate) => {
    const id = candidate.id;
    const text = candidate.resume_text ||
      `Name: ${candidate.full_name}\nTitle: ${candidate.current_title}\nExperience: ${candidate.experience_years} years\nSkills: ${candidate.skills}\nEducation: ${candidate.education}\nApplied Role: ${candidate.applied_role}`;
    setAuthLoading(p => ({ ...p, [id]: true }));
    setAuthError(p => ({ ...p, [id]: null }));
    try {
      const result = await callAuthenticityCheck(text, candidate.applied_role || "");
      setAuthData(p => ({ ...p, [id]: result }));
    } catch (e) {
      setAuthError(p => ({ ...p, [id]: e.message }));
    }
    setAuthLoading(p => ({ ...p, [id]: false }));
  };

  // -- Boolean Search + Ranking Engine ----------------------
  const parseBoolTerms = (str) => str.split(",").map(s => s.trim().toLowerCase()).filter(Boolean);

  const candidateMatchesSearch = (c) => {
    const searchable = [c.full_name, c.current_title, c.skills, c.applied_role, c.location, c.education, c.notes, c.email].filter(Boolean).join(" ").toLowerCase();

    if (searchMode === "simple") {
      const q = candidateSearch.toLowerCase().trim();
      return !q || searchable.includes(q);
    }

    // Boolean mode
    const must    = parseBoolTerms(boolQuery.must);
    const should  = parseBoolTerms(boolQuery.should);
    const mustNot = parseBoolTerms(boolQuery.mustNot);

    // MUST - all required terms must match
    if (must.length > 0 && !must.every(t => searchable.includes(t))) return false;
    // MUST NOT - none of these can match
    if (mustNot.length > 0 && mustNot.some(t => searchable.includes(t))) return false;
    // SHOULD - at least one must match (if any specified)
    if (should.length > 0 && !should.some(t => searchable.includes(t))) return false;
    // Score range
    if (c.ai_score !== null) {
      if (c.ai_score < boolQuery.minScore || c.ai_score > boolQuery.maxScore) return false;
    }
    // Experience range
    const exp = parseInt(c.experience_years) || 0;
    if (boolQuery.minExp && exp < parseInt(boolQuery.minExp)) return false;
    if (boolQuery.maxExp && exp > parseInt(boolQuery.maxExp)) return false;
    // Location filter
    if (boolQuery.location && !c.location?.toLowerCase().includes(boolQuery.location.toLowerCase())) return false;
    // Role filter
    if (boolQuery.role && !c.applied_role?.toLowerCase().includes(boolQuery.role.toLowerCase())) return false;
    return true;
  };

  const candidateMatchesFilter = (c) => {
    if (candidateFilter === "all")       return true;
    if (candidateFilter === "validated") return c.ai_validated;
    if (candidateFilter === "pending")   return !c.ai_validated;
    if (candidateFilter === "qualified") return c.ai_score >= 70;
    if (candidateFilter === "maybe")     return c.ai_score >= 50 && c.ai_score < 70;
    if (candidateFilter === "rejected")  return c.ai_validated && c.ai_score < 50;
    return true;
  };

  // Relevance score for ranking (0-100 range, multiple signals)
  const candidateRelevanceScore = (c) => {
    let score = 0;
    // AI evaluation score (weight: 40%)
    if (c.ai_score !== null) score += (c.ai_score / 100) * 40;
    // Auth score (weight: 20%)
    if (c.auth_score !== null) score += (c.auth_score / 100) * 20;
    // Boolean SHOULD matches boost relevance
    if (searchMode === "boolean") {
      const searchable = [c.full_name, c.current_title, c.skills, c.applied_role].filter(Boolean).join(" ").toLowerCase();
      const should = parseBoolTerms(boolQuery.should);
      const mustTerms = parseBoolTerms(boolQuery.must);
      const allTerms = [...mustTerms, ...should];
      if (allTerms.length > 0) {
        const matches = allTerms.filter(t => searchable.includes(t)).length;
        score += (matches / allTerms.length) * 40;
      } else { score += 40; }
    } else { score += 40; }
    return Math.round(score);
  };

  const filteredCandidates = candidates
    .filter(c => candidateMatchesSearch(c) && candidateMatchesFilter(c))
    .map(c => ({ ...c, _relevance: candidateRelevanceScore(c) }))
    .sort((a, b) => {
      let aVal, bVal;
      if (sortBy === "score")     { aVal = a.ai_score ?? -1;          bVal = b.ai_score ?? -1; }
      else if (sortBy === "auth") { aVal = a.auth_score ?? -1;         bVal = b.auth_score ?? -1; }
      else if (sortBy === "name") { aVal = a.full_name?.toLowerCase(); bVal = b.full_name?.toLowerCase(); }
      else if (sortBy === "exp")  { aVal = parseInt(a.experience_years) || 0; bVal = parseInt(b.experience_years) || 0; }
      else if (sortBy === "relevance") { aVal = a._relevance;          bVal = b._relevance; }
      else                        { aVal = new Date(a.created_at);     bVal = new Date(b.created_at); }
      if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

  const handleTFile = async (f) => {
    setTFile(f); setTFileErr(""); setTFileData(null);
    try {
      const d = await extractFile(f);
      if (d.type === "pdf") { setTFileErr("Use DOCX, ODT, or TXT for training."); setTFile(null); return; }
      setTFileData(d);
    } catch (e) { setTFileErr(e.message); setTFile(null); }
  };

  const handleTrain = async () => {
    const text = tMode === "upload" ? tFileData?.text : tPasted;
    if (!text?.trim() || !tFeedback.trim()) { setErr("Fill in both resume content and evaluation notes."); return; }
    setErr(""); setTrainSaving(true);

    // Auto-run authenticity check in parallel while saving
    const [saveResult, authResult] = await Promise.allSettled([
      saveTrainingExample({ resume: text, feedback: tFeedback, score: tScore, verdict: tVerdict }),
      authEnabled ? callAuthenticityCheck(text, "") : Promise.resolve(null),
    ]);

    const saved = saveResult.status === "fulfilled" ? saveResult.value : false;
    const auth  = authResult.status  === "fulfilled" ? authResult.value  : null;

    setTrainSaving(false);

    if (saved) {
      // Auto-populate auth result if it ran
      if (auth) setTAuthData(auth);
      setTPasted(""); setTFeedback(""); setTScore(70); setTVerdict("Yes");
      setTFile(null); setTFileData(null);
      setTrainSaved(true); setTimeout(() => setTrainSaved(false), 2500);
    } else {
      setErr("Failed to save. Check your Supabase connection.");
    }
  };

  // -- Loading screen ----------------------------------------
  if (sessionLoading) {
    return (
      <div style={{ minHeight: "100vh", background: "#ffffff", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Mono',monospace", color: "#1A56DB", fontSize: 12, letterSpacing: 2 }}>
        LOADING...
      </div>
    );
  }

  // -- Login screen ------------------------------------------
  if (!user) return <LoginScreen onLogin={setUser} />;

  // -- Main UI -----------------------------------------------
  return (
    <div style={{ minHeight: "100vh", background: "#ffffff", color: "#111827", fontFamily: "Calibri, sans-serif" }}>
      <style>{`
        *{box-sizing:border-box;margin:0;padding:0}
        ::-webkit-scrollbar{width:4px}::-webkit-scrollbar-track{background:#F3F4F6}::-webkit-scrollbar-thumb{background:#D1D5DB;border-radius:4px}
        textarea,input{resize:vertical}
        .ltab{background:none;border:none;cursor:pointer;font-family:Calibri,sans-serif;font-size:13px;font-weight:600;padding:10px 14px;color:#6B7280;transition:all .2s;border-bottom:3px solid transparent;white-space:nowrap}
        .ltab.on{color:#1A56DB;border-bottom-color:#1A56DB;background:#EFF6FF}
        .ltab:hover:not(.on){color:#1A56DB;background:#F5F8FF}
        .lbtn{background:#1A56DB;color:#ffffff;border:none;cursor:pointer;font-family:Calibri,sans-serif;font-weight:700;font-size:14px;padding:11px 20px;border-radius:8px;transition:all .2s;white-space:nowrap}
        .lbtn:hover{background:#1447BB}.lbtn:disabled{background:#9CA3AF;cursor:not-allowed}
        .lbtn-orange{background:#F97316!important;}.lbtn-orange:hover{background:#EA6C0A!important}
        .lghost{background:none;border:1.5px solid #D1D5DB;color:#6B7280;cursor:pointer;font-family:Calibri,sans-serif;font-size:13px;padding:8px 14px;border-radius:8px;transition:all .2s}
        .lghost:hover{border-color:#1A56DB;color:#1A56DB;background:#EFF6FF}
        .linput{background:#F9FAFB;border:1.5px solid #D1D5DB;color:#111827;font-family:Calibri,sans-serif;font-size:15px;padding:11px 14px;border-radius:8px;width:100%;transition:border-color .2s;line-height:1.6;outline:none}
        .linput:focus{border-color:#1A56DB;background:#ffffff}.linput::placeholder{color:#9CA3AF}
        .rcard{animation:fadeUp .4s ease forwards}
        @keyframes fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
        .vpill{display:inline-block;padding:4px 10px;border-radius:20px;font-size:11px;font-weight:700}
        .sdot::before{content:'▸';margin-right:8px;color:#1A56DB;font-size:12px}
        .cdot::before{content:'▸';margin-right:8px;color:#EF4444;font-size:12px}
        .pulse{animation:pulseA 2s ease-in-out infinite}
        @keyframes pulseA{0%,100%{opacity:.4}50%{opacity:1}}
        .slider{-webkit-appearance:none;width:100%;height:4px;background:linear-gradient(to right,#1A56DB 0%,#1A56DB var(--v),#E5E7EB var(--v),#E5E7EB 100%);border-radius:4px;outline:none;cursor:pointer}
        .slider::-webkit-slider-thumb{-webkit-appearance:none;width:18px;height:18px;background:#1A56DB;border-radius:50%;cursor:pointer;border:2px solid #fff;box-shadow:0 2px 4px rgba(26,86,219,0.3)}
        .tcard{background:#ffffff;border:1.5px solid #E5E7EB;border-radius:10px;padding:14px 18px;transition:all .2s;cursor:pointer}
        .tcard:hover{border-color:#1A56DB;background:#F5F8FF}
        .hrow{background:#ffffff;border:1.5px solid #E5E7EB;border-radius:10px;padding:12px 16px;display:flex;align-items:center;gap:14px;cursor:pointer;transition:all .2s}
        .hrow:hover{border-color:#1A56DB;background:#F5F8FF}
        h1,h2,h3{font-family:Calibri,sans-serif;font-weight:700;color:#000000}
        h1{font-size:24px}h2{font-size:20px}h3{font-size:17px}
        .mob-hide{display:flex}.mob-email{display:block}
        .nav-tabs{display:flex;overflow-x:auto;-webkit-overflow-scrolling:touch;scrollbar-width:none}
        .nav-tabs::-webkit-scrollbar{display:none}
        .page-pad{padding:40px}
        .slot-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px}
        .stats-grid{display:grid;grid-template-columns:repeat(5,1fr);gap:12px}
        .two-col{display:grid;grid-template-columns:1fr 1fr;gap:14px}
        .train-grid{display:grid;grid-template-columns:1fr 320px;gap:24px}
        .header-right{display:flex;align-items:center;gap:12px}
        .eval-header{display:flex;align-items:flex-end;justify-content:space-between;margin-bottom:28px}
        .eval-actions{display:flex;gap:10px;align-items:center}
        @media(max-width:768px){
          .mob-hide{display:none!important}
          .mob-email{display:none!important}
          .page-pad{padding:16px}
          .slot-grid{grid-template-columns:1fr!important}
          .stats-grid{grid-template-columns:repeat(3,1fr)!important}
          .two-col{grid-template-columns:1fr!important}
          .train-grid{grid-template-columns:1fr!important}
          .header-right{gap:6px}
          .eval-header{flex-direction:column;align-items:flex-start;gap:12px}
          .eval-actions{width:100%}
          .eval-actions .lbtn{width:100%;text-align:center}
          h1{font-size:20px}h2{font-size:18px}h3{font-size:16px}
          .ltab{font-size:12px;padding:10px 12px}
        }
        @media(max-width:480px){
          .stats-grid{grid-template-columns:repeat(2,1fr)!important}
          .page-pad{padding:12px}
        }
      `}</style>

      {/* -- HEADER -- */}
      <div style={{ borderBottom: "2px solid #E8F0FE", padding: "0 16px", background: "#ffffff", boxShadow: "0 2px 8px rgba(26,86,219,0.06)" }}>
        <div style={{ maxWidth: 1040, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {/* Logo */}
            <div style={{ background: "#1A56DB", borderRadius: 10, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <span style={{ color: "#ffffff", fontWeight: 700, fontSize: 16 }}>L</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", width: 110 }}>
              <div style={{ fontFamily: "Calibri, sans-serif", fontWeight: 700, fontSize: 22, color: "#F97316", letterSpacing: "-1px", lineHeight: 1 }}>
                LYCA<span style={{ color: "#1A56DB" }}>360</span>
              </div>
              <div style={{ fontFamily: "Calibri, sans-serif", fontSize: 8, color: "#000000", fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", width: "100%", textAlign: "justify", lineHeight: 1.4 }}>
                Staffing Intelligence
              </div>
            </div>
          </div>
          <div className="header-right">
            <div className="mob-hide" style={{ fontSize: 12, color: "#6B7280", background: "#F3F4F6", padding: "4px 10px", borderRadius: 20, border: "1px solid #E5E7EB" }}>
              {examplesLoading ? "..." : `${examples.length} trained`}
            </div>
            {/* Authenticity toggle */}
            <div className="mob-hide" style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 10px", borderRadius: 20, background: authEnabled ? "#FFF7ED" : "#F3F4F6", border: `1.5px solid ${authEnabled ? "#F97316" : "#E5E7EB"}`, cursor: "pointer" }} onClick={() => toggleAuth(!authEnabled)}>
              <div style={{ width: 24, height: 14, borderRadius: 7, background: authEnabled ? "#F97316" : "#D1D5DB", position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
                <div style={{ position: "absolute", top: 2, left: authEnabled ? 12 : 2, width: 10, height: 10, borderRadius: "50%", background: "#fff", transition: "left 0.2s" }}/>
              </div>
              <span style={{ fontSize: 12, color: authEnabled ? "#F97316" : "#9CA3AF", fontWeight: 600, whiteSpace: "nowrap" }}>Auth {authEnabled ? "ON" : "OFF"}</span>
            </div>
            <div className="mob-email" style={{ fontSize: 12, color: "#6B7280" }}>{user.email}</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: isAdmin ? "#1A56DB" : "#F97316", background: isAdmin ? "#EFF6FF" : "#FFF7ED", border: `1.5px solid ${isAdmin ? "#BFDBFE" : "#FED7AA"}`, padding: "4px 10px", borderRadius: 20, whiteSpace: "nowrap" }}>
              {isAdmin ? "⚙ Admin" : "👤 Recruiter"}
            </div>
            <button className="lghost" onClick={handleSignOut} style={{ fontSize: 12, padding: "5px 12px", color: "#EF4444", borderColor: "#FCA5A5" }}>Sign Out</button>
          </div>
        </div>
        <div className="nav-tabs" style={{ maxWidth: 1040, margin: "0 auto" }}>
          {[
            ["evaluate", "Batch Evaluate", true],
            ["candidates", "Candidates", true],
            ["train", "Train Engine", isAdmin],
            ["library", `Library (${examples.length})`, isAdmin],
            ["history", "History", true],
            ["admin", "⚙ Admin", isAdmin],
          ].filter(([,, allowed]) => allowed).map(([id, label]) => (
            <button key={id} className={`ltab${tab === id ? " on" : ""}`} onClick={() => { setTab(id); setErr(""); }}>{label}</button>
          ))}
        </div>
      </div>

      <div className="page-pad" style={{ maxWidth: 1040, margin: "0 auto" }}>

        {/* -- BATCH EVALUATE -- */}
        {tab === "evaluate" && (
          <div>
            {examples.length === 0 && (
              <div style={{ background: "#F9FAFB", border: "1.5px solid #FED7AA", borderLeft: "3px solid #F97316", padding: "12px 18px", borderRadius: 2, marginBottom: 28, fontSize: 11, color: "#F97316", letterSpacing: 1 }}>
                ▲ NO TRAINING DATA - Add examples in the Train tab first.
              </div>
            )}
            <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 28 }}>
              <div>
                <div style={{ fontFamily: "'Outfit',sans-serif", fontWeight: 700, fontSize: 20, fontWeight: 700, color: "#000000", marginBottom: 6 }}>Batch Evaluation</div>
                <div style={{ fontSize: 11, color: "#6B7280", letterSpacing: 1 }}>Evaluate 1 to {MAX_SLOTS} resumes at once . ranked by score</div>
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                {slots.length < MAX_SLOTS && (
                  <button className="lghost" onClick={addSlot} style={{ borderColor: "#BFDBFE", color: "#1A56DB", fontSize: 11 }}>+ Add Slot ({slots.length}/{MAX_SLOTS})</button>
                )}
                <input className="linput" placeholder="Role / Position (optional)" value={jobRole} onChange={e => setJobRole(e.target.value)} style={{ width: 240, padding: "9px 14px" }} />
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 24 }}>
              {slots.map((slot, idx) => {
                const hasContent = slot.mode === "paste" ? slot.pastedText.trim() : slot.fileData;
                return (
                  <div key={slot.id} style={{ background: "#F3F4F6", border: `1px solid ${slot.status === "done" ? "#00E5A025" : slot.status === "error" ? "#F8717125" : "#1e1e3a"}`, borderRadius: 2, padding: "16px 18px" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ width: 22, height: 22, borderRadius: "50%", background: hasContent ? "#00E5A020" : "#111", border: `1px solid ${hasContent ? "#00E5A050" : "#1e1e3a"}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: hasContent ? "#00E5A0" : "#444", fontWeight: 700 }}>{idx + 1}</div>
                        <input className="linput" value={slot.name} onChange={e => updateSlot(slot.id, { name: e.target.value })} placeholder={`Candidate ${idx + 1}`} style={{ width: 140, padding: "5px 10px", fontSize: 11, border: "none", background: "transparent", borderBottom: "1px solid #1e1e3a", borderRadius: 0 }} />
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        {slot.status === "loading" && <span className="pulse" style={{ fontSize: 10, color: "#FACC15" }}>◉</span>}
                        {slot.status === "done" && <span style={{ fontSize: 10, color: "#1A56DB" }}>✓</span>}
                        {slot.status === "error" && <span style={{ fontSize: 10, color: "#F87171" }}>✕ Error</span>}
                        {slots.length > MIN_SLOTS && (
                          <button onClick={() => removeSlot(slot.id)} style={{ background: "none", border: "none", color: "#9CA3AF", cursor: "pointer", fontSize: 16 }}
                            onMouseOver={e => e.target.style.color = "#F87171"} onMouseOut={e => e.target.style.color = "#333"}>x</button>
                        )}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
                      {[["paste", "✎ Paste"], ["upload", "⬆ Upload"]].map(([m, label]) => (
                        <button key={m} onClick={() => updateSlot(slot.id, { mode: m })} style={{ background: slot.mode === m ? "#00E5A015" : "none", border: `1px solid ${slot.mode === m ? "#93C5FD" : "#1e1e3a"}`, color: slot.mode === m ? "#00E5A0" : "#555", cursor: "pointer", fontFamily: "'DM Mono',monospace", fontSize: 9, letterSpacing: 1, textTransform: "uppercase", padding: "5px 12px", borderRadius: 2 }}>{label}</button>
                      ))}
                    </div>
                    {slot.mode === "paste" ? (
                      <textarea className="linput" placeholder="Paste resume text..." value={slot.pastedText} onChange={e => updateSlot(slot.id, { pastedText: e.target.value })} rows={5} style={{ fontSize: 11 }} />
                    ) : (
                      <div>
                        {!slot.file ? (
                          <div onClick={() => document.getElementById(`fi-${slot.id}`)?.click()} style={{ border: "1.5px dashed #1e1e3a", borderRadius: 2, padding: "18px", textAlign: "center", cursor: "pointer", background: "#ffffff", fontSize: 11, color: "#9CA3AF" }}>
                            Click or drop file . PDF DOCX ODT TXT
                            <input id={`fi-${slot.id}`} type="file" accept=".pdf,.docx,.odt,.odf,.txt" style={{ display: "none" }} onChange={e => { if (e.target.files[0]) handleSlotFile(slot.id, e.target.files[0]); e.target.value = ""; }} />
                          </div>
                        ) : (
                          <div style={{ background: "#ffffff", border: "1px solid #00E5A025", borderRadius: 2, padding: "10px 12px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div style={{ fontSize: 11, color: slot.fileLoading ? "#FACC15" : "#D4D4E8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "85%" }}>
                              {slot.fileLoading ? "◉ Extracting..." : slot.fileData ? `✓ ${slot.file.name}` : slot.fileErr}
                            </div>
                            <button onClick={() => updateSlot(slot.id, { file: null, fileData: null, fileErr: "" })} style={{ background: "none", border: "none", color: "#6B7280", cursor: "pointer", fontSize: 16 }}>x</button>
                          </div>
                        )}
                      </div>
                    )}
                    {slot.result && (
                      <div>
                        <div onClick={() => setSelectedSlot(selectedSlot?.id === slot.id ? null : slot)} style={{ marginTop: 10, padding: "8px 12px", background: VS[slot.result.verdict]?.dim || "#111", border: `1px solid ${VS[slot.result.verdict]?.accent || "#333"}30`, borderRadius: 2, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <SmallScore score={slot.result.score} />
                            <span style={{ fontSize: 9, color: VS[slot.result.verdict]?.accent, letterSpacing: 2, textTransform: "uppercase" }}>{slot.result.verdict}</span>
                          </div>
                          <span style={{ fontSize: 9, color: "#6B7280" }}>{selectedSlot?.id === slot.id ? "▲" : "▼"}</span>
                        </div>
                        {/* Web search mini button on slot */}
                        {slot.name.trim() && (
                          <button onClick={() => handleSlotWebSearch(slot)} disabled={slot.webLoading}
                            style={{ marginTop: 6, width: "100%", background: slot.webData ? "#0a0a14" : "none", border: `1px solid ${slot.webData ? "#4A90E240" : "#1e1e3a"}`, color: slot.webData ? "#4A90E2" : "#444", cursor: "pointer", fontFamily: "'DM Mono',monospace", fontSize: 9, letterSpacing: 1, textTransform: "uppercase", padding: "6px 12px", borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                            <span>🌐</span>
                            {slot.webLoading ? "Searching..." : slot.webData ? "✓ Web Intel . View ▼" : "Google Search →"}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {batchErr && <div style={{ color: "#F87171", fontSize: 11, marginBottom: 14, letterSpacing: 1 }}>✕ {batchErr}</div>}
            <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 32 }}>
              <button className="lbtn" onClick={handleBatchEvaluate} disabled={batchLoading} style={{ fontSize: 13 }}>
                {batchLoading ? "Evaluating..." : `Evaluate ${slots.filter(s => s.mode === "paste" ? s.pastedText.trim() : s.fileData).length || ""} ${slots.filter(s => s.mode === "paste" ? s.pastedText.trim() : s.fileData).length === 1 ? "Resume" : "Resumes"} →`}
              </button>
              {batchLoading && <div style={{ fontSize: 10, color: "#1A56DB", letterSpacing: 2 }}><span className="pulse" style={{ display: "inline-block" }}>◉</span> Running in parallel...</div>}
              {batchDone && !batchLoading && <div style={{ fontSize: 11, color: "#1A56DB" }}>✓ {rankedSlots.length} evaluated . saved to history</div>}
            </div>

            {/* Expanded detail */}
            {selectedSlot?.result && (() => {
              const svs = VS[selectedSlot.result.verdict] || VS["Maybe"];
              return (
                <div style={{ marginBottom: 32, background: "#F3F4F6", border: `1px solid ${svs.accent}30`, borderTop: `3px solid ${svs.accent}`, borderRadius: 2, padding: "24px 28px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                    <div style={{ fontFamily: "'Outfit',sans-serif", fontWeight: 700, fontSize: 16, color: "#000000" }}>{selectedSlot.name || "Candidate"}</div>
                    <div className="vpill" style={{ background: svs.dim, color: svs.accent, border: `1px solid ${svs.accent}40` }}>{selectedSlot.result.verdict}</div>
                    <div style={{ fontSize: 10, color: "#9CA3AF", letterSpacing: 2, textTransform: "uppercase" }}>Confidence: {selectedSlot.result.confidence}</div>
                  </div>
                  <div style={{ fontSize: 13, lineHeight: 1.7, color: "#374151", marginBottom: 14 }}>{selectedSlot.result.summary}</div>
                  {selectedSlot.result.standout && <div style={{ fontSize: 11, color: "#F97316", background: "#FFF7ED", border: "1px solid #FED7AA", padding: "8px 14px", borderRadius: 6, marginBottom: 14 }}>★ {selectedSlot.result.standout}</div>}
                  <div className="two-col" style={{ display: "grid", gap: 14, marginBottom: 14 }}>
                    <div style={{ background: "#ffffff", border: "1.5px solid #BFDBFE", borderRadius: 8, padding: "16px 18px" }}>
                      <div style={{ fontSize: 11, color: "#000000", fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", marginBottom: 10 }}>Strengths</div>
                      {(selectedSlot.result.strengths || []).map((s, i) => <div key={i} className="sdot" style={{ fontSize: 13, color: "#374151", marginBottom: 8, lineHeight: 1.5 }}>{s}</div>)}
                    </div>
                    <div style={{ background: "#ffffff", border: "1.5px solid #FCA5A5", borderRadius: 8, padding: "16px 18px" }}>
                      <div style={{ fontSize: 11, color: "#000000", fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", marginBottom: 10 }}>Concerns</div>
                      {(selectedSlot.result.concerns || []).map((c, i) => <div key={i} className="cdot" style={{ fontSize: 13, color: "#374151", marginBottom: 8, lineHeight: 1.5 }}>{c}</div>)}
                    </div>
                  </div>
                  <div style={{ fontSize: 13, color: "#6B7280", lineHeight: 1.8, marginBottom: 16 }}>{selectedSlot.result.recommendation}</div>

                  {/* Experience Breakdown */}
                  {(selectedSlot.result.experience_breakdown || []).length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#000000", marginBottom: 12, paddingBottom: 8, borderBottom: "2px solid #E5E7EB" }}>Work Experience Breakdown</div>
                      {(selectedSlot.result.experience_breakdown || []).map((exp, i) => (
                        <div key={i} style={{ background: "#ffffff", border: `1.5px solid ${exp.tech_issues?.length > 0 ? "#FCA5A5" : "#E5E7EB"}`, borderLeft: `4px solid ${exp.tech_issues?.length > 0 ? "#EF4444" : "#1A56DB"}`, borderRadius: 8, padding: "14px 16px", marginBottom: 10 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
                            <span style={{ fontFamily: "Calibri, sans-serif", fontSize: 15, fontWeight: 700, color: "#000000" }}>{exp.company}</span>
                            <span style={{ background: "#EFF6FF", color: "#1A56DB", border: "1px solid #BFDBFE", padding: "2px 10px", borderRadius: 20, fontSize: 12, fontWeight: 700 }}>{exp.role}</span>
                            <span style={{ background: "#F3F4F6", color: "#6B7280", border: "1px solid #E5E7EB", padding: "2px 10px", borderRadius: 20, fontSize: 12 }}>{exp.years}</span>
                            {exp.responsibilities_valid === false && <span style={{ background: "#FEF2F2", color: "#EF4444", border: "1px solid #FCA5A5", padding: "2px 10px", borderRadius: 20, fontSize: 12, fontWeight: 700 }}>⚠ Role Mismatch</span>}
                          </div>
                          {exp.tech_issues?.length > 0 && (
                            <div style={{ background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: 6, padding: "8px 12px", marginBottom: 6 }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: "#EF4444", marginBottom: 4 }}>⚠ Technology Issues Found</div>
                              {exp.tech_issues.map((issue, j) => (
                                <div key={j} style={{ fontSize: 12, color: "#374151", marginBottom: 3 }}>* {issue}</div>
                              ))}
                            </div>
                          )}
                          {exp.notes && <div style={{ fontSize: 12, color: "#6B7280", fontStyle: "italic" }}>{exp.notes}</div>}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Timeline Issues */}
                  {(selectedSlot.result.timeline_issues || []).length > 0 && (
                    <div style={{ background: "#FFF7ED", border: "1.5px solid #FED7AA", borderRadius: 8, padding: "14px 16px", marginBottom: 14 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#F97316", marginBottom: 8 }}>⚠ Timeline Issues</div>
                      {(selectedSlot.result.timeline_issues || []).map((issue, i) => (
                        <div key={i} style={{ fontSize: 13, color: "#374151", marginBottom: 4 }}>* {issue}</div>
                      ))}
                    </div>
                  )}

                  {/* Tech Anachronisms */}
                  {(selectedSlot.result.tech_anachronisms || []).length > 0 && (
                    <div style={{ background: "#FEF2F2", border: "1.5px solid #FCA5A5", borderRadius: 8, padding: "14px 16px", marginBottom: 14 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#EF4444", marginBottom: 8 }}>🚨 Technology Fraud Detected</div>
                      {(selectedSlot.result.tech_anachronisms || []).map((item, i) => (
                        <div key={i} style={{ fontSize: 13, color: "#374151", marginBottom: 4 }}>* {item}</div>
                      ))}
                    </div>
                  )}
                  {/* Web Intelligence in expanded batch slot */}
                  <div style={{ borderTop: "1px solid #E5E7EB", paddingTop: 16, marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#000000", letterSpacing: 1, textTransform: "uppercase", marginBottom: 12 }}>-- Google Web Intelligence</div>
                    <WebIntelPanel
                      data={selectedSlot.webData}
                      loading={selectedSlot.webLoading}
                      error={selectedSlot.webError}
                      onSearch={() => handleSlotWebSearch(selectedSlot)}
                      candidateName={selectedSlot.name || "this candidate"}
                      compact={false}
                    />
                  </div>
                  {/* Authenticity Check in expanded batch slot */}
                  <div style={{ borderTop: "1px solid #E5E7EB", paddingTop: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#000000", letterSpacing: 1, textTransform: "uppercase", marginBottom: 12 }}>-- Resume Authenticity Check</div>
                    <AuthenticityPanel
                      data={selectedSlot.authData}
                      loading={selectedSlot.authLoading}
                      error={selectedSlot.authError}
                      onCheck={() => handleSlotAuthCheck(selectedSlot)}
                      resumeAvailable={!!(selectedSlot.mode === "paste" ? selectedSlot.pastedText?.trim() : selectedSlot.fileData)}
                    />
                  </div>
                </div>
              );
            })()}

            {/* Leaderboard */}
            {batchDone && rankedSlots.length > 0 && (
              <div ref={resultRef}>
                <div style={{ fontSize: 9, color: "#9CA3AF", letterSpacing: 3, textTransform: "uppercase", marginBottom: 20 }}>-- Ranked Results</div>
                {rankedSlots.length >= 3 && (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1.1fr 1fr", gap: 12, marginBottom: 14, alignItems: "flex-end" }}>
                    {[rankedSlots[1], rankedSlots[0], rankedSlots[2]].map((slot, pi) => {
                      const rank = pi === 1 ? 1 : pi === 0 ? 2 : 3;
                      const svs = VS[slot.result.verdict] || VS["Maybe"];
                      return (
                        <div key={slot.id} onClick={() => setSelectedSlot(selectedSlot?.id === slot.id ? null : slot)}
                          style={{ background: rank === 1 ? "#0e1f18" : "#0a0a16", border: `1px solid ${svs.accent}${rank === 1 ? "50" : "25"}`, borderTop: `3px solid ${svs.accent}`, borderRadius: 2, padding: "20px 16px", textAlign: "center", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                          <div style={{ fontSize: 18 }}>{["🥇","🥈","🥉"][rank-1]}</div>
                          <div style={{ fontFamily: "'DM Mono',monospace", fontWeight: 700, fontSize: 26, color: scoreColor(slot.result.score) }}>{slot.result.score}</div>
                          <div style={{ fontSize: 12, color: "#111827", fontFamily: "Calibri, sans-serif", fontWeight: 700 }}>{slot.name || `Candidate ${slots.indexOf(slot)+1}`}</div>
                          <div className="vpill" style={{ background: svs.dim, color: svs.accent, border: `1px solid ${svs.accent}40` }}>{slot.result.verdict}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
                  {rankedSlots.slice(3).map((slot, idx) => {
                    const svs = VS[slot.result.verdict] || VS["Maybe"];
                    return (
                      <div key={slot.id} className="hrow" onClick={() => setSelectedSlot(selectedSlot?.id === slot.id ? null : slot)}>
                        <div style={{ fontSize: 10, color: "#9CA3AF", width: 20 }}>#{idx+4}</div>
                        <div style={{ fontFamily: "'DM Mono',monospace", fontWeight: 700, fontSize: 18, color: scoreColor(slot.result.score), minWidth: 36 }}>{slot.result.score}</div>
                        <div style={{ flex: 1, fontSize: 13, color: "#374151", fontFamily: "'Outfit',sans-serif", fontWeight: 500 }}>{slot.name || `Candidate ${slots.indexOf(slot)+1}`}</div>
                        <div className="vpill" style={{ background: svs.dim, color: svs.accent, border: `1px solid ${svs.accent}40` }}>{slot.result.verdict}</div>
                      </div>
                    );
                  })}
                </div>
                <button className="lghost" onClick={() => { setSlots(Array.from({ length: MIN_SLOTS }, emptySlot)); setBatchDone(false); setSelectedSlot(null); }}>Clear All & Start Over</button>
              </div>
            )}
          </div>
        )}

        {/* -- TRAIN -- */}
        {tab === "train" && (
          <div>
            {!isSupabaseConfigured && (
              <div style={{ background: "#140a00", border: "1px solid #FACC1540", borderLeft: "3px solid #FACC15", borderRadius: 2, padding: "14px 18px", marginBottom: 20, display: "flex", alignItems: "flex-start", gap: 12 }}>
                <span style={{ fontSize: 16, flexShrink: 0 }}>⚠</span>
                <div>
                  <div style={{ fontSize: 12, color: "#FACC15", fontWeight: 600, marginBottom: 4 }}>Supabase not configured - Training examples won't be saved</div>
                  <div style={{ fontSize: 11, color: "#6B7280", lineHeight: 1.7 }}>
                    Open <span style={{ color: "#1A56DB", fontFamily: "'DM Mono',monospace" }}>src/supabaseClient.js</span> and fill in your Supabase credentials to enable saving training data.
                  </div>
                </div>
              </div>
            )}
            <div style={{ marginBottom: 28 }}>
              <div style={{ fontFamily: "'Outfit',sans-serif", fontWeight: 700, fontSize: 20, fontWeight: 700, color: "#000000", marginBottom: 8 }}>Train Lyca 360 to think like you</div>
              <div style={{ fontSize: 12, color: "#6B7280", lineHeight: 1.7 }}>Each example you add is saved to your account permanently. The more you teach it, the sharper it gets.</div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#000000", letterSpacing: 1, textTransform: "uppercase", marginBottom: 10 }}>Resume Input</div>
              <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                {[["paste", "✎  Paste Text"], ["upload", "⬆  Upload File"]].map(([m, label]) => (
                  <button key={m} onClick={() => setTMode(m)} style={{ background: tMode === m ? "#00E5A015" : "none", border: `1px solid ${tMode === m ? "#93C5FD" : "#1e1e3a"}`, color: tMode === m ? "#00E5A0" : "#555", cursor: "pointer", fontFamily: "'DM Mono',monospace", fontSize: 10, letterSpacing: 2, textTransform: "uppercase", padding: "7px 16px", borderRadius: 2 }}>{label}</button>
                ))}
              </div>
              {tMode === "paste" ? (
                <textarea className="linput" placeholder="Paste the full resume text here..." value={tPasted} onChange={e => setTPasted(e.target.value)} rows={9} />
              ) : (
                <div>
                  {!tFile ? (
                    <div onClick={() => document.getElementById("tfinp")?.click()} style={{ border: "2px dashed #1e1e3a", borderRadius: 2, padding: "30px", textAlign: "center", cursor: "pointer", background: "#F3F4F6", fontSize: 12, color: "#6B7280" }}>
                      Click to upload . DOCX ODT TXT
                      <input id="tfinp" type="file" accept=".docx,.odt,.odf,.txt" style={{ display: "none" }} onChange={e => { if (e.target.files[0]) handleTFile(e.target.files[0]); e.target.value = ""; }} />
                    </div>
                  ) : (
                    <div style={{ background: "#F3F4F6", border: "1px solid #00E5A030", borderRadius: 2, padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 12, color: "#111827" }}>{tFileData ? `✓ ${tFile.name}` : tFileErr}</span>
                      <button onClick={() => { setTFile(null); setTFileData(null); setTFileErr(""); }} style={{ background: "none", border: "none", color: "#6B7280", cursor: "pointer", fontSize: 16 }}>x</button>
                    </div>
                  )}
                  {tFileErr && <div style={{ color: "#F87171", fontSize: 11, marginTop: 8 }}>✕ {tFileErr}</div>}
                </div>
              )}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#000000", letterSpacing: 1, textTransform: "uppercase", marginBottom: 8 }}>Your Score: <span style={{ color: scoreColor(tScore) }}>{tScore}</span></div>
                <input type="range" min={0} max={100} value={tScore} className="slider" style={{ "--v": `${tScore}%` }} onChange={e => setTScore(Number(e.target.value))} />
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: "#9CA3AF", marginTop: 6 }}><span>0 - Reject</span><span>50 - Neutral</span><span>100 - Hire</span></div>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#000000", letterSpacing: 1, textTransform: "uppercase", marginBottom: 8 }}>Your Verdict</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {VERDICTS.map(v => { const s = VS[v]; const on = tVerdict === v; return <button key={v} onClick={() => setTVerdict(v)} style={{ background: on ? s.dim : "none", border: `1px solid ${on ? s.accent : "#1e1e3a"}`, color: on ? s.accent : "#555", fontFamily: "'DM Mono',monospace", fontSize: 10, letterSpacing: 1, textTransform: "uppercase", padding: "6px 12px", borderRadius: 2, cursor: "pointer" }}>{v}</button>; })}
                </div>
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#000000", letterSpacing: 1, textTransform: "uppercase", marginBottom: 8 }}>Your Evaluation Notes</div>
              <textarea className="linput" placeholder="Why did you score them this way? What stood out - good or bad?" value={tFeedback} onChange={e => setTFeedback(e.target.value)} rows={5} />
            </div>

            {err && <div style={{ color: "#F87171", fontSize: 11, marginBottom: 14, letterSpacing: 1 }}>✕ {err}</div>}
            <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 32 }}>
              <button className="lbtn" onClick={handleTrain} disabled={trainSaving}>{trainSaving ? "Saving to Supabase..." : "Save Training Example →"}</button>
              {trainSaved && <div style={{ fontSize: 11, color: "#1A56DB", letterSpacing: 2 }}>✓ Saved to your account</div>}
            </div>

            {/* -- WEB INTELLIGENCE in Train Engine -- */}
            <div style={{ borderTop: "1px solid #E5E7EB", paddingTop: 28 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 9, color: "#4A90E2", letterSpacing: 3, textTransform: "uppercase", marginBottom: 4 }}>🌐 Google Web Intelligence</div>
                  <div style={{ fontSize: 11, color: "#6B7280", lineHeight: 1.6 }}>Search a candidate's name on Google to check their online presence before adding them as a training example.</div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
                <input
                  className="linput"
                  placeholder="Enter candidate full name to search..."
                  value={tCandidateName}
                  onChange={e => setTCandidateName(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleTrainWebSearch()}
                  style={{ flex: 1, fontSize: 11, padding: "10px 14px" }}
                />
                <button
                  onClick={handleTrainWebSearch}
                  disabled={tWebLoading || !tCandidateName.trim()}
                  style={{ background: "#F3F4F6", border: "1px solid #4A90E240", color: "#4A90E2", cursor: tWebLoading || !tCandidateName.trim() ? "not-allowed" : "pointer", fontFamily: "'DM Mono',monospace", fontWeight: 500, fontSize: 11, letterSpacing: 1.5, textTransform: "uppercase", padding: "10px 22px", borderRadius: 2, whiteSpace: "nowrap", opacity: tWebLoading || !tCandidateName.trim() ? 0.5 : 1 }}>
                  {tWebLoading ? "Searching..." : "🌐 Search Google →"}
                </button>
              </div>
              <WebIntelPanel
                data={tWebData}
                loading={tWebLoading}
                error={tWebError}
                onSearch={handleTrainWebSearch}
                candidateName={tCandidateName || "candidate"}
                compact={false}
              />
            </div>

            {/* -- AUTHENTICITY CHECK in Train Engine -- */}
            <div style={{ borderTop: "1px solid #E5E7EB", paddingTop: 28, marginTop: 8 }}>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 9, color: "#FACC15", letterSpacing: 3, textTransform: "uppercase", marginBottom: 6 }}>🔬 Resume Authenticity Check</div>
                <div style={{ fontSize: 11, color: "#6B7280", lineHeight: 1.6 }}>
                  Before training on this resume, verify it's real. Checks technology timelines, stack compatibility, role alignment, and fake-resume patterns.
                </div>
              </div>
              <div style={{ marginBottom: 14 }}>
                <button
                  onClick={handleTrainAuthCheck}
                  disabled={tAuthLoading || (!(tMode === "paste" ? tPasted.trim() : tFileData))}
                  style={{ background: "#F9FAFB", border: "1px solid #FACC1540", color: "#FACC15", cursor: tAuthLoading ? "not-allowed" : "pointer", fontFamily: "'DM Mono',monospace", fontWeight: 500, fontSize: 11, letterSpacing: 1.5, textTransform: "uppercase", padding: "11px 24px", borderRadius: 2, whiteSpace: "nowrap", opacity: tAuthLoading || !(tMode === "paste" ? tPasted.trim() : tFileData) ? 0.5 : 1 }}>
                  {tAuthLoading ? "🔬 Analysing..." : "🔬 Run Authenticity Check →"}
                </button>
              </div>
              <AuthenticityPanel
                data={tAuthData}
                loading={tAuthLoading}
                error={tAuthError}
                onCheck={handleTrainAuthCheck}
                resumeAvailable={!!(tMode === "paste" ? tPasted.trim() : tFileData)}
              />
            </div>
          </div>
        )}

        {/* -- LIBRARY -- */}
        {tab === "library" && (
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28 }}>
              <div>
                <div style={{ fontFamily: "'Outfit',sans-serif", fontWeight: 700, fontSize: 20, fontWeight: 700, color: "#000000", marginBottom: 4 }}>Training Library</div>
                <div style={{ fontSize: 11, color: "#6B7280" }}>{examples.length} examples . stored in your Supabase account</div>
              </div>
            </div>
            {examplesLoading ? (
              <div style={{ fontSize: 11, color: "#6B7280", letterSpacing: 2 }}>Loading from database...</div>
            ) : examples.length === 0 ? (
              <div style={{ textAlign: "center", padding: "80px 40px", border: "1px dashed #1e1e3a", borderRadius: 2 }}>
                <div style={{ fontSize: 32, marginBottom: 16, opacity: 0.3 }}>◎</div>
                <div style={{ fontSize: 12, color: "#9CA3AF", lineHeight: 1.8 }}>No training examples yet.<br />Go to <span style={{ color: "#1A56DB" }}>Train Engine</span> to start.</div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {examples.map((ex, i) => {
                  const s = VS[ex.verdict] || VS["Maybe"]; const open = expanded === ex.id;
                  return (
                    <div key={ex.id} className="tcard" onClick={() => setExpanded(open ? null : ex.id)}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                          <div style={{ fontSize: 9, color: "#9CA3AF", width: 20 }}>#{i+1}</div>
                          <div className="vpill" style={{ background: s.dim, color: s.accent, border: `1px solid ${s.accent}30` }}>{ex.verdict}</div>
                          <div style={{ fontSize: 13, fontWeight: 500, color: scoreColor(ex.score) }}>{ex.score}<span style={{ fontSize: 9, color: "#9CA3AF" }}>/100</span></div>
                          <div style={{ fontSize: 10, color: "#9CA3AF" }}>{new Date(ex.created_at).toLocaleDateString()}</div>
                        </div>
                        <div style={{ display: "flex", gap: 10 }}>
                          <div style={{ fontSize: 10, color: "#9CA3AF" }}>{open ? "▲" : "▼"}</div>
                          <button className="lghost" style={{ padding: "4px 10px", fontSize: 10 }}
                            onClick={e => { e.stopPropagation(); if (window.confirm("Remove this example?")) deleteTrainingExample(ex.id); }}>Remove</button>
                        </div>
                      </div>
                      {open && (
                        <div style={{ marginTop: 14, borderTop: "1px solid #E5E7EB", paddingTop: 14 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: "#000000", letterSpacing: 1, textTransform: "uppercase", marginBottom: 8 }}>Resume</div>
                          <div style={{ fontSize: 11, color: "#666", lineHeight: 1.7, maxHeight: 120, overflow: "auto", background: "#ffffff", padding: "10px 14px", borderRadius: 2, marginBottom: 12 }}>{ex.resume}</div>
                          <div style={{ fontSize: 11, fontWeight: 700, color: "#000000", letterSpacing: 1, textTransform: "uppercase", marginBottom: 8 }}>Your Notes</div>
                          <div style={{ fontSize: 12, color: "#6B7280", lineHeight: 1.7, fontStyle: "italic" }}>"{ex.feedback}"</div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* -- CANDIDATE DATABASE -- */}
        {tab === "candidates" && (
          <div>
            {/* Setup notice */}
            {!isSupabaseConfigured && (
              <div style={{ background: "#140a00", border: "1px solid #FACC1540", borderLeft: "3px solid #FACC15", borderRadius: 2, padding: "14px 18px", marginBottom: 20, display: "flex", alignItems: "flex-start", gap: 12 }}>
                <span style={{ fontSize: 16, flexShrink: 0 }}>⚠</span>
                <div>
                  <div style={{ fontSize: 12, color: "#FACC15", fontWeight: 600, marginBottom: 4 }}>Supabase not configured - Candidate Database is in demo mode</div>
                  <div style={{ fontSize: 11, color: "#6B7280", lineHeight: 1.7 }}>
                    To save and load real candidates, open <span style={{ color: "#1A56DB", fontFamily: "'DM Mono',monospace" }}>src/supabaseClient.js</span> and replace <span style={{ color: "#FACC15", fontFamily: "'DM Mono',monospace" }}>YOUR_SUPABASE_URL</span> and <span style={{ color: "#FACC15", fontFamily: "'DM Mono',monospace" }}>YOUR_SUPABASE_ANON_KEY</span> with your credentials from supabase.com → Your Project → Settings → API
                  </div>
                </div>
              </div>
            )}
            {/* Header */}
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 28 }}>
              <div>
                <div style={{ fontFamily: "'Outfit',sans-serif", fontWeight: 700, fontSize: 20, fontWeight: 700, color: "#000000", marginBottom: 6 }}>Candidate Database</div>
                <div style={{ fontSize: 11, color: "#6B7280", letterSpacing: 1 }}>
                  {candidates.length} candidates . {candidates.filter(c => c.ai_validated).length} AI validated
                </div>
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button className="lghost" onClick={loadCandidates} style={{ fontSize: 10, padding: "8px 14px" }}>↻ Refresh</button>
                <button className="lbtn" onClick={() => setShowAddCandidate(!showAddCandidate)} style={{ padding: "10px 24px", fontSize: 11 }}>
                  {showAddCandidate ? "✕ Cancel" : "+ Add Candidate"}
                </button>
              </div>
            </div>

            {/* Add Candidate Form */}
            {showAddCandidate && (
              <div style={{ background: "#F3F4F6", border: "1px solid #00E5A030", borderTop: "3px solid #00E5A0", borderRadius: 2, padding: "28px 28px", marginBottom: 28 }} className="rcard">
                <div style={{ fontFamily: "'Outfit',sans-serif", fontWeight: 700, fontSize: 16, color: "#000000", marginBottom: 20 }}>New Candidate</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 14 }}>
                  {[
                    ["full_name", "Full Name *", "John Smith"],
                    ["email", "Email", "john@email.com"],
                    ["phone", "Phone", "+1 555 000 0000"],
                    ["location", "Location", "New York, USA"],
                    ["current_title", "Current Title", "Senior Developer"],
                    ["experience_years", "Years Experience", "5"],
                    ["education", "Education", "B.S. Computer Science"],
                    ["applied_role", "Applying For", "Lead Engineer"],
                    ["linkedin", "LinkedIn", "linkedin.com/in/johnsmith"],
                  ].map(([field, label, ph]) => (
                    <div key={field}>
                      <div style={{ fontSize: 9, color: "#6B7280", letterSpacing: 3, textTransform: "uppercase", marginBottom: 6 }}>{label}</div>
                      <input className="linput" placeholder={ph} value={newCandidate[field]}
                        onChange={e => setNewCandidate(p => ({ ...p, [field]: e.target.value }))} style={{ fontSize: 11, padding: "9px 12px" }} />
                    </div>
                  ))}
                </div>
                <div className="two-col" style={{ display: "grid", gap: 14, marginBottom: 14 }}>
                  <div>
                    <div style={{ fontSize: 9, color: "#6B7280", letterSpacing: 3, textTransform: "uppercase", marginBottom: 6 }}>Key Skills</div>
                    <input className="linput" placeholder="React, Node.js, Python, AWS..." value={newCandidate.skills}
                      onChange={e => setNewCandidate(p => ({ ...p, skills: e.target.value }))} style={{ fontSize: 11, padding: "9px 12px" }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 9, color: "#6B7280", letterSpacing: 3, textTransform: "uppercase", marginBottom: 6 }}>Notes</div>
                    <input className="linput" placeholder="Recruiter notes, source, referral..." value={newCandidate.notes}
                      onChange={e => setNewCandidate(p => ({ ...p, notes: e.target.value }))} style={{ fontSize: 11, padding: "9px 12px" }} />
                  </div>
                </div>
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 9, color: "#6B7280", letterSpacing: 3, textTransform: "uppercase", marginBottom: 6 }}>
                    Resume / Profile Text <span style={{ color: "#9CA3AF" }}>- paste full resume for best AI validation</span>
                  </div>
                  <textarea className="linput" placeholder="Paste the full resume text here for AI evaluation. If blank, AI will evaluate based on the fields above." value={newCandidate.resume_text}
                    onChange={e => setNewCandidate(p => ({ ...p, resume_text: e.target.value }))} rows={6} style={{ fontSize: 11 }} />
                </div>
                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  <button className="lbtn" onClick={handleAddCandidate} disabled={savingCandidate || !newCandidate.full_name.trim()}>
                    {savingCandidate ? "Saving..." : "Save Candidate →"}
                  </button>
                  <div style={{ fontSize: 11, color: "#6B7280", letterSpacing: 1 }}>You can validate with AI after saving</div>
                </div>
              </div>
            )}

            {/* -- SEARCH + FILTER BAR -- */}
            <div style={{ background: "#F3F4F6", border: "1.5px solid #E5E7EB", borderRadius: 2, marginBottom: 20, overflow: "hidden" }}>
              {/* Search mode toggle */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: "1px solid #E5E7EB" }}>
                <div style={{ display: "flex", gap: 6 }}>
                  {[["simple", "🔍 Simple Search"], ["boolean", "⚡ Boolean Search"]].map(([m, label]) => (
                    <button key={m} onClick={() => setSearchMode(m)}
                      style={{ background: searchMode === m ? "#00E5A015" : "none", border: `1px solid ${searchMode === m ? "#93C5FD" : "#1e1e3a"}`, color: searchMode === m ? "#00E5A0" : "#555", cursor: "pointer", fontFamily: "'DM Mono',monospace", fontSize: 10, letterSpacing: 1, textTransform: "uppercase", padding: "6px 14px", borderRadius: 2, transition: "all 0.15s" }}>
                      {label}
                    </button>
                  ))}
                </div>
                {/* Results count */}
                <div style={{ fontSize: 10, color: "#6B7280", letterSpacing: 1 }}>
                  {filteredCandidates.length} of {candidates.length} candidates
                </div>
              </div>

              {/* Simple search */}
              {searchMode === "simple" && (
                <div style={{ padding: "12px 16px", display: "flex", gap: 10, alignItems: "center" }}>
                  <input className="linput" placeholder="Search by name, title, skills, location, role..."
                    value={candidateSearch} onChange={e => setCandidateSearch(e.target.value)}
                    style={{ flex: 1, fontSize: 12, padding: "9px 14px" }} />
                </div>
              )}

              {/* Boolean search */}
              {searchMode === "boolean" && (
                <div style={{ padding: "16px" }}>
                  <div style={{ fontSize: 9, color: "#6B7280", letterSpacing: 2, marginBottom: 12 }}>
                    Separate multiple terms with commas. Example: MUST → "React, TypeScript" means candidate must have BOTH.
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 12 }}>
                    <div>
                      <div style={{ fontSize: 9, color: "#1A56DB", letterSpacing: 3, textTransform: "uppercase", marginBottom: 6 }}>✓ MUST Have (AND)</div>
                      <input className="linput" placeholder="e.g. React, Python, AWS" style={{ fontSize: 11 }}
                        value={boolQuery.must} onChange={e => setBoolQuery(p => ({ ...p, must: e.target.value }))} />
                      <div style={{ fontSize: 9, color: "#9CA3AF", marginTop: 4 }}>Candidate must have ALL of these</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 9, color: "#FACC15", letterSpacing: 3, textTransform: "uppercase", marginBottom: 6 }}>~ SHOULD Have (OR)</div>
                      <input className="linput" placeholder="e.g. Docker, Kubernetes" style={{ fontSize: 11 }}
                        value={boolQuery.should} onChange={e => setBoolQuery(p => ({ ...p, should: e.target.value }))} />
                      <div style={{ fontSize: 9, color: "#9CA3AF", marginTop: 4 }}>Candidate must have at least ONE</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 9, color: "#F87171", letterSpacing: 3, textTransform: "uppercase", marginBottom: 6 }}>✕ MUST NOT Have (NOT)</div>
                      <input className="linput" placeholder="e.g. PHP, WordPress" style={{ fontSize: 11 }}
                        value={boolQuery.mustNot} onChange={e => setBoolQuery(p => ({ ...p, mustNot: e.target.value }))} />
                      <div style={{ fontSize: 9, color: "#9CA3AF", marginTop: 4 }}>Exclude if candidate has ANY of these</div>
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
                    <div>
                      <div style={{ fontSize: 9, color: "#6B7280", letterSpacing: 2, textTransform: "uppercase", marginBottom: 6 }}>Location</div>
                      <input className="linput" placeholder="e.g. London, USA" style={{ fontSize: 11 }}
                        value={boolQuery.location} onChange={e => setBoolQuery(p => ({ ...p, location: e.target.value }))} />
                    </div>
                    <div>
                      <div style={{ fontSize: 9, color: "#6B7280", letterSpacing: 2, textTransform: "uppercase", marginBottom: 6 }}>Role Applied For</div>
                      <input className="linput" placeholder="e.g. Senior Engineer" style={{ fontSize: 11 }}
                        value={boolQuery.role} onChange={e => setBoolQuery(p => ({ ...p, role: e.target.value }))} />
                    </div>
                    <div>
                      <div style={{ fontSize: 9, color: "#6B7280", letterSpacing: 2, textTransform: "uppercase", marginBottom: 6 }}>Experience (years)</div>
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <input className="linput" placeholder="Min" style={{ fontSize: 11, textAlign: "center" }}
                          value={boolQuery.minExp} onChange={e => setBoolQuery(p => ({ ...p, minExp: e.target.value }))} />
                        <span style={{ color: "#6B7280", fontSize: 11 }}>-</span>
                        <input className="linput" placeholder="Max" style={{ fontSize: 11, textAlign: "center" }}
                          value={boolQuery.maxExp} onChange={e => setBoolQuery(p => ({ ...p, maxExp: e.target.value }))} />
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 9, color: "#6B7280", letterSpacing: 2, textTransform: "uppercase", marginBottom: 6 }}>AI Score Range</div>
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <input className="linput" placeholder="0" style={{ fontSize: 11, textAlign: "center" }}
                          value={boolQuery.minScore || ""} onChange={e => setBoolQuery(p => ({ ...p, minScore: Number(e.target.value) || 0 }))} />
                        <span style={{ color: "#6B7280", fontSize: 11 }}>-</span>
                        <input className="linput" placeholder="100" style={{ fontSize: 11, textAlign: "center" }}
                          value={boolQuery.maxScore === 100 ? "" : boolQuery.maxScore} onChange={e => setBoolQuery(p => ({ ...p, maxScore: Number(e.target.value) || 100 }))} />
                      </div>
                    </div>
                  </div>
                  <button onClick={() => setBoolQuery({ must: "", should: "", mustNot: "", minScore: 0, maxScore: 100, minExp: "", maxExp: "", location: "", role: "" })}
                    style={{ background: "none", border: "1px solid #2a2a4a", color: "#6B7280", cursor: "pointer", fontFamily: "'DM Mono',monospace", fontSize: 10, letterSpacing: 1, textTransform: "uppercase", padding: "5px 14px", borderRadius: 2 }}>
                    Clear All
                  </button>
                </div>
              )}

              {/* Filter tabs + Sort controls */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", borderTop: "1px solid #E5E7EB", flexWrap: "wrap", gap: 8 }}>
                {/* Filter pills */}
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {[["all","All"],["validated","✓ Validated"],["pending","⏳ Pending"],["qualified","★ Qualified"],["maybe","~ Maybe"],["rejected","✕ Rejected"]].map(([v, label]) => (
                    <button key={v} onClick={() => setCandidateFilter(v)}
                      style={{ background: candidateFilter === v ? "#00E5A015" : "none", border: `1px solid ${candidateFilter === v ? "#93C5FD" : "#1e1e3a"}`, color: candidateFilter === v ? "#00E5A0" : "#555", cursor: "pointer", fontFamily: "'DM Mono',monospace", fontSize: 9, letterSpacing: 1, textTransform: "uppercase", padding: "5px 12px", borderRadius: 2, whiteSpace: "nowrap" }}>
                      {label}
                    </button>
                  ))}
                </div>
                {/* Sort controls */}
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 9, color: "#9CA3AF", letterSpacing: 2, textTransform: "uppercase" }}>Sort by</span>
                  {[["score","AI Score"],["auth","Auth"],["relevance","Relevance"],["exp","Experience"],["name","Name"],["date","Date Added"]].map(([v, label]) => (
                    <button key={v} onClick={() => { if (sortBy === v) setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortBy(v); setSortDir("desc"); } }}
                      style={{ background: sortBy === v ? "#4A90E215" : "none", border: `1px solid ${sortBy === v ? "#4A90E240" : "#1e1e3a"}`, color: sortBy === v ? "#4A90E2" : "#555", cursor: "pointer", fontFamily: "'DM Mono',monospace", fontSize: 9, letterSpacing: 1, textTransform: "uppercase", padding: "5px 10px", borderRadius: 2, whiteSpace: "nowrap" }}>
                      {label}{sortBy === v ? (sortDir === "desc" ? " ↓" : " ↑") : ""}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Boolean search active indicator */}
            {searchMode === "boolean" && (boolQuery.must || boolQuery.should || boolQuery.mustNot || boolQuery.location || boolQuery.role || boolQuery.minExp || boolQuery.maxExp) && (
              <div style={{ marginBottom: 16, padding: "10px 16px", background: "#0a0a14", border: "1px solid #00E5A020", borderRadius: 2, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                <span style={{ fontSize: 10, color: "#6B7280", letterSpacing: 1, textTransform: "uppercase" }}>Active filters:</span>
                {boolQuery.must && <span style={{ fontSize: 10, color: "#1A56DB", background: "#EFF6FF", border: "1px solid #00E5A030", padding: "3px 10px", borderRadius: 2 }}>✓ MUST: {boolQuery.must}</span>}
                {boolQuery.should && <span style={{ fontSize: 10, color: "#FACC15", background: "#FACC1510", border: "1px solid #FACC1530", padding: "3px 10px", borderRadius: 2 }}>~ SHOULD: {boolQuery.should}</span>}
                {boolQuery.mustNot && <span style={{ fontSize: 10, color: "#F87171", background: "#F8717110", border: "1px solid #F8717130", padding: "3px 10px", borderRadius: 2 }}>✕ NOT: {boolQuery.mustNot}</span>}
                {boolQuery.location && <span style={{ fontSize: 10, color: "#6B7280", background: "#1e1e3a", padding: "3px 10px", borderRadius: 2 }}>📍 {boolQuery.location}</span>}
                {boolQuery.role && <span style={{ fontSize: 10, color: "#6B7280", background: "#1e1e3a", padding: "3px 10px", borderRadius: 2 }}>→ {boolQuery.role}</span>}
                {boolQuery.minExp && <span style={{ fontSize: 10, color: "#6B7280", background: "#1e1e3a", padding: "3px 10px", borderRadius: 2 }}>⏱ {boolQuery.minExp}{boolQuery.maxExp ? `-${boolQuery.maxExp}` : "+"} yrs</span>}
                <span style={{ marginLeft: "auto", fontSize: 10, color: "#1A56DB", fontWeight: 600 }}>{filteredCandidates.length} match{filteredCandidates.length !== 1 ? "es" : ""}</span>
              </div>
            )}

            {/* Stats bar */}
            {candidates.length > 0 && (
              <div className="stats-grid" style={{ display: "grid", gap: 10, marginBottom: 24 }}>
                {[
                  ["Total", candidates.length, "#00E5A0"],
                  ["Validated", candidates.filter(c => c.ai_validated).length, "#4ADE80"],
                  ["Qualified (70+)", candidates.filter(c => c.ai_score >= 70).length, "#00E5A0"],
                  ["Maybe (50-69)", candidates.filter(c => c.ai_score >= 50 && c.ai_score < 70).length, "#FACC15"],
                  ["Rejected (<50)", candidates.filter(c => c.ai_validated && c.ai_score < 50).length, "#F87171"],
                ].map(([label, count, color]) => (
                  <div key={label} style={{ background: "#F3F4F6", border: "1.5px solid #E5E7EB", borderRadius: 2, padding: "12px 16px", textAlign: "center" }}>
                    <div style={{ fontFamily: "'DM Mono',monospace", fontWeight: 700, fontSize: 22, color, marginBottom: 4 }}>{count}</div>
                    <div style={{ fontSize: 9, color: "#6B7280", letterSpacing: 1, textTransform: "uppercase" }}>{label}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Candidate list */}
            {candidatesLoading ? (
              <div style={{ fontSize: 11, color: "#6B7280", letterSpacing: 2, textAlign: "center", padding: 40 }}>Loading candidates...</div>
            ) : filteredCandidates.length === 0 ? (
              <div style={{ textAlign: "center", padding: "60px 40px", border: "1px dashed #1e1e3a", borderRadius: 2 }}>
                <div style={{ fontSize: 32, marginBottom: 14, opacity: 0.3 }}>◎</div>
                <div style={{ fontSize: 12, color: "#9CA3AF", lineHeight: 1.8 }}>
                  {candidates.length === 0 ? <>No candidates yet.<br />Click <span style={{ color: "#1A56DB" }}>+ Add Candidate</span> to start building your database.</> : "No candidates match your search."}
                </div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {filteredCandidates.map(c => {
                  const svs = c.ai_verdict ? (VS[c.ai_verdict] || VS["Maybe"]) : null;
                  const isExpanded = expandedCandidate === c.id;
                  const isValidating = validatingId === c.id;

                  return (
                    <div key={c.id} style={{ background: "#F3F4F6", border: `1px solid ${c.ai_validated ? (svs?.accent + "30" || "#1e1e3a") : "#1e1e3a"}`, borderRadius: 2, overflow: "hidden", transition: "border-color 0.2s" }}>

                      {/* Candidate row */}
                      <div style={{ padding: "16px 20px", display: "flex", alignItems: "center", gap: 16 }}>

                        {/* Rank position + relevance */}
                        <div style={{ flexShrink: 0, textAlign: "center", minWidth: 36 }}>
                          <div style={{ fontSize: 9, color: "#9CA3AF", letterSpacing: 1 }}>#{filteredCandidates.indexOf(c) + 1}</div>
                          {searchMode === "boolean" && (boolQuery.must || boolQuery.should) && (
                            <div style={{ fontSize: 9, color: "#4A90E2", background: "#4A90E215", border: "1px solid #4A90E230", padding: "2px 5px", borderRadius: 2, marginTop: 2, whiteSpace: "nowrap" }}>
                              {c._relevance}% rel
                            </div>
                          )}
                        </div>

                        {/* AI Score ring */}
                        <div style={{ flexShrink: 0 }}>
                          {c.ai_validated ? (
                            <SmallScore score={c.ai_score} />
                          ) : (
                            <div style={{ width: 44, height: 44, borderRadius: "50%", border: "2px dashed #1e1e3a", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <span style={{ fontSize: 8, color: "#9CA3AF", textAlign: "center", lineHeight: 1.2 }}>NOT<br />RATED</span>
                            </div>
                          )}
                        </div>

                        {/* Name + details */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4, flexWrap: "wrap" }}>
                            <div style={{ fontFamily: "Calibri, sans-serif", fontWeight: 700, fontSize: 15, color: "#000000" }}>{c.full_name}</div>
                            {c.ai_validated && svs && (
                              <div className="vpill" style={{ background: svs.dim, color: svs.accent, border: `1px solid ${svs.accent}40` }}>{c.ai_verdict}</div>
                            )}
                            {c.ai_validated && c.ai_confidence && (
                              <div style={{ fontSize: 9, color: "#9CA3AF", letterSpacing: 1, textTransform: "uppercase" }}>Confidence: {c.ai_confidence}</div>
                            )}
                          </div>
                          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                            {c.current_title && <span style={{ fontSize: 11, color: "#6B7280" }}>💼 {c.current_title}</span>}
                            {c.experience_years && <span style={{ fontSize: 11, color: "#6B7280" }}>⏱ {c.experience_years}yr exp</span>}
                            {c.location && <span style={{ fontSize: 11, color: "#6B7280" }}>📍 {c.location}</span>}
                            {c.applied_role && <span style={{ fontSize: 11, color: "#00E5A080" }}>→ {c.applied_role}</span>}
                          </div>
                          {c.skills && <div style={{ fontSize: 10, color: "#6B7280", marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.skills}</div>}
                        </div>

                        {/* Action buttons */}
                        <div style={{ display: "flex", gap: 8, flexShrink: 0, alignItems: "center" }}>
                          {/* VALIDATE BUTTON */}
                          <button
                            onClick={() => handleValidateCandidate(c)}
                            disabled={isValidating}
                            style={{
                              background: c.ai_validated ? "#00E5A010" : "#00E5A0",
                              color: c.ai_validated ? "#00E5A0" : "#06060E",
                              border: c.ai_validated ? "1px solid #00E5A040" : "none",
                              cursor: isValidating ? "not-allowed" : "pointer",
                              fontFamily: "'DM Mono',monospace", fontWeight: 500, fontSize: 10,
                              letterSpacing: 1.5, textTransform: "uppercase",
                              padding: "8px 16px", borderRadius: 2,
                              transition: "all 0.2s", whiteSpace: "nowrap",
                              opacity: isValidating ? 0.6 : 1,
                            }}
                          >
                            {isValidating ? (
                              <span><span className="pulse" style={{ display: "inline-block" }}>◉</span> Validating...</span>
                            ) : c.ai_validated ? (
                              "↻ Re-Validate"
                            ) : (
                              "⚡ Validate"
                            )}
                          </button>

                          {/* Expand / collapse */}
                          <button
                            onClick={() => setExpandedCandidate(isExpanded ? null : c.id)}
                            style={{ background: "none", border: "1.5px solid #E5E7EB", color: "#666", cursor: "pointer", fontFamily: "'DM Mono',monospace", fontSize: 10, padding: "8px 12px", borderRadius: 2, letterSpacing: 1 }}>
                            {isExpanded ? "▲ Less" : "▼ More"}
                          </button>

                          {/* Delete */}
                          <button onClick={() => handleDeleteCandidate(c.id)}
                            style={{ background: "none", border: "1.5px solid #E5E7EB", color: "#9CA3AF", cursor: "pointer", fontFamily: "'DM Mono',monospace", fontSize: 13, padding: "7px 11px", borderRadius: 2 }}
                            onMouseOver={e => { e.currentTarget.style.borderColor = "#F87171"; e.currentTarget.style.color = "#F87171"; }}
                            onMouseOut={e => { e.currentTarget.style.borderColor = "#1e1e3a"; e.currentTarget.style.color = "#444"; }}>
                            x
                          </button>
                        </div>
                      </div>

                      {/* AI Validation result bar - shown even when collapsed */}
                      {c.ai_validated && c.ai_summary && (
                        <div style={{ padding: "10px 20px", background: svs?.dim || "#111", borderTop: `1px solid ${svs?.accent || "#00E5A0"}20`, fontSize: 11, color: "#4B5563", lineHeight: 1.6 }}>
                          <span style={{ color: svs?.accent || "#00E5A0", fontSize: 9, letterSpacing: 2, textTransform: "uppercase", marginRight: 10 }}>AI SUMMARY</span>
                          {c.ai_summary}
                          {c.ai_standout && <span style={{ marginLeft: 12, color: "#FACC15" }}>★ {c.ai_standout}</span>}
                        </div>
                      )}

                      {/* Expanded detail */}
                      {isExpanded && (
                        <div style={{ padding: "20px 20px", borderTop: "1px solid #E5E7EB", background: "#07070f" }}>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 20 }}>
                            {[
                              ["Email", c.email], ["Phone", c.phone], ["Location", c.location],
                              ["Education", c.education], ["LinkedIn", c.linkedin], ["Applied For", c.applied_role],
                              ["Experience", c.experience_years ? `${c.experience_years} years` : "-"], ["Added", new Date(c.created_at).toLocaleDateString()], ["AI Validated", c.ai_validated_at ? new Date(c.ai_validated_at).toLocaleDateString() : "Not yet"],
                            ].map(([label, value]) => value ? (
                              <div key={label}>
                                <div style={{ fontSize: 9, color: "#9CA3AF", letterSpacing: 2, textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
                                <div style={{ fontSize: 11, color: "#6B7280" }}>{value}</div>
                              </div>
                            ) : null)}
                          </div>

                          {c.notes && (
                            <div style={{ marginBottom: 20 }}>
                              <div style={{ fontSize: 9, color: "#9CA3AF", letterSpacing: 2, textTransform: "uppercase", marginBottom: 6 }}>Recruiter Notes</div>
                              <div style={{ fontSize: 11, color: "#6B7280", fontStyle: "italic" }}>{c.notes}</div>
                            </div>
                          )}

                          {/* Full AI evaluation */}
                          {c.ai_validated && (
                            <div style={{ borderTop: "1px solid #E5E7EB", paddingTop: 20 }}>
                              <div style={{ fontSize: 9, color: svs?.accent || "#00E5A0", letterSpacing: 3, textTransform: "uppercase", marginBottom: 16 }}>-- AI Evaluation Detail</div>
                              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
                                <div style={{ background: "#F3F4F6", border: "1px solid #00E5A020", borderRadius: 2, padding: "14px 16px" }}>
                                  <div style={{ fontSize: 11, fontWeight: 700, color: "#000000", letterSpacing: 1, textTransform: "uppercase", marginBottom: 10 }}>Strengths</div>
                                  {(c.ai_strengths || []).map((s, i) => <div key={i} className="sdot" style={{ fontSize: 11, color: "#4B5563", marginBottom: 7, lineHeight: 1.5 }}>{s}</div>)}
                                </div>
                                <div style={{ background: "#F3F4F6", border: "1px solid #F8717120", borderRadius: 2, padding: "14px 16px" }}>
                                  <div style={{ fontSize: 11, fontWeight: 700, color: "#000000", letterSpacing: 1, textTransform: "uppercase", marginBottom: 10 }}>Concerns</div>
                                  {(c.ai_concerns || []).map((s, i) => <div key={i} className="cdot" style={{ fontSize: 11, color: "#4B5563", marginBottom: 7, lineHeight: 1.5 }}>{s}</div>)}
                                </div>
                              </div>
                              <div style={{ background: "#F3F4F6", border: "1.5px solid #E5E7EB", borderRadius: 2, padding: "14px 16px" }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: "#000000", letterSpacing: 1, textTransform: "uppercase", marginBottom: 8 }}>Recommendation</div>
                                <div style={{ fontSize: 12, color: "#6B7280", lineHeight: 1.8 }}>{c.ai_recommendation}</div>
                              </div>
                            </div>
                          )}

                          {/* Validate prompt if not done */}
                          {!c.ai_validated && (
                            <div style={{ textAlign: "center", padding: "24px 0", border: "1px dashed #1e1e3a", borderRadius: 2 }}>
                              <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 14 }}>This candidate hasn't been validated yet.</div>
                              <button className="lbtn" onClick={() => handleValidateCandidate(c)} disabled={isValidating} style={{ padding: "11px 28px", fontSize: 11 }}>
                                {isValidating ? "⚡ Validating..." : "⚡ Validate with AI Now"}
                              </button>
                            </div>
                          )}

                          {/* -- WEB INTELLIGENCE -- */}
                          <div style={{ marginTop: 20, borderTop: "1px solid #E5E7EB", paddingTop: 20 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: "#000000", letterSpacing: 1, textTransform: "uppercase", marginBottom: 12 }}>-- Google Web Intelligence</div>
                            <WebIntelPanel
                              data={webData[c.id] || (c.web_searched ? {
                                results: c.web_results || [],
                                analysis: {
                                  online_presence: c.web_online_presence,
                                  sentiment: c.web_sentiment,
                                  red_flags: c.web_red_flags || [],
                                  positive_signals: c.web_positive_signals || [],
                                  platforms_found: c.web_platforms || [],
                                  summary: c.web_summary,
                                  recommendation: c.web_recommendation,
                                },
                                query: c.web_query,
                              } : null)}
                              loading={webLoading[c.id]}
                              error={webError[c.id]}
                              onSearch={() => handleWebSearch(c)}
                              candidateName={c.full_name}
                              compact={false}
                            />
                          </div>

                          {/* -- AUTHENTICITY CHECK -- */}
                          <div style={{ marginTop: 20, borderTop: "1px solid #E5E7EB", paddingTop: 20 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: "#000000", letterSpacing: 1, textTransform: "uppercase", marginBottom: 12 }}>-- Resume Authenticity Check</div>
                            <AuthenticityPanel
                              data={authData[c.id]}
                              loading={authLoading[c.id]}
                              error={authError[c.id]}
                              onCheck={() => handleAuthCheck(c)}
                              resumeAvailable={!!(c.resume_text || c.skills || c.current_title)}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* -- HISTORY -- */}
        {tab === "history" && (
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28 }}>
              <div>
                <div style={{ fontFamily: "'Outfit',sans-serif", fontWeight: 700, fontSize: 20, fontWeight: 700, color: "#000000", marginBottom: 4 }}>Evaluation History</div>
                <div style={{ fontSize: 11, color: "#6B7280" }}>Every resume ever evaluated - stored permanently in Supabase</div>
              </div>
              <button className="lghost" onClick={loadHistory} style={{ fontSize: 10 }}>↻ Refresh</button>
            </div>
            {historyLoading ? (
              <div style={{ fontSize: 11, color: "#6B7280", letterSpacing: 2 }}>Loading history...</div>
            ) : history.length === 0 ? (
              <div style={{ textAlign: "center", padding: "80px 40px", border: "1px dashed #1e1e3a", borderRadius: 2 }}>
                <div style={{ fontSize: 32, marginBottom: 16, opacity: 0.3 }}>◎</div>
                <div style={{ fontSize: 12, color: "#9CA3AF", lineHeight: 1.8 }}>No evaluations yet.<br />Run a batch evaluation to see history here.</div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {history.map(ev => {
                  const svs = VS[ev.verdict] || VS["Maybe"];
                  return (
                    <div key={ev.id} className="hrow">
                      <SmallScore score={ev.score} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontFamily: "Calibri, sans-serif", fontWeight: 700, fontSize: 13, color: "#000000", marginBottom: 2 }}>{ev.candidate_name}</div>
                        <div style={{ fontSize: 10, color: "#6B7280" }}>{ev.job_role || "No role specified"} . {new Date(ev.created_at).toLocaleDateString()}</div>
                      </div>
                      <div className="vpill" style={{ background: svs.dim, color: svs.accent, border: `1px solid ${svs.accent}40` }}>{ev.verdict}</div>
                      <div style={{ fontSize: 10, color: "#9CA3AF", letterSpacing: 1, textTransform: "uppercase" }}>{ev.confidence}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

        {/* -- ADMIN PANEL -- */}
        {tab === "admin" && isAdmin && (
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 32 }}>
              <div>
                <div style={{ fontFamily: "'Outfit',sans-serif", fontWeight: 700, fontSize: 20, fontWeight: 700, color: "#000000", marginBottom: 6 }}>
                  ⚙ Admin Panel
                </div>
                <div style={{ fontSize: 11, color: "#6B7280" }}>Manage team access . create accounts . assign roles</div>
              </div>
              <button className="lghost" onClick={loadAdminUsers} style={{ fontSize: 10 }}>↻ Refresh</button>
            </div>

            {/* Create new user */}
            <div style={{ background: "#F3F4F6", border: "1px solid #00E5A030", borderTop: "3px solid #00E5A0", borderRadius: 2, padding: "24px 28px", marginBottom: 28 }}>
              <div style={{ fontFamily: "'Outfit',sans-serif", fontWeight: 700, fontSize: 16, color: "#000000", marginBottom: 20 }}>
                + Create New Team Member
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 180px", gap: 14, marginBottom: 14 }}>
                <div>
                  <div style={{ fontSize: 9, color: "#6B7280", letterSpacing: 3, textTransform: "uppercase", marginBottom: 6 }}>Email Address</div>
                  <input className="linput" type="email" placeholder="recruiter@company.com"
                    value={newUserEmail} onChange={e => setNewUserEmail(e.target.value)}
                    style={{ fontSize: 12 }}/>
                </div>
                <div>
                  <div style={{ fontSize: 9, color: "#6B7280", letterSpacing: 3, textTransform: "uppercase", marginBottom: 6 }}>Temporary Password</div>
                  <input className="linput" type="password" placeholder="Min 6 characters"
                    value={newUserPassword} onChange={e => setNewUserPassword(e.target.value)}
                    style={{ fontSize: 12 }}/>
                </div>
                <div>
                  <div style={{ fontSize: 9, color: "#6B7280", letterSpacing: 3, textTransform: "uppercase", marginBottom: 6 }}>Role</div>
                  <select className="linput" value={newUserRole} onChange={e => setNewUserRole(e.target.value)} style={{ fontSize: 12, cursor: "pointer" }}>
                    <option value="recruiter">Recruiter</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>

              {/* Role descriptions */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
                <div style={{ padding: "10px 14px", background: newUserRole === "recruiter" ? "#00E5A010" : "#0e0e1c", border: `1px solid ${newUserRole === "recruiter" ? "#BFDBFE" : "#1e1e3a"}`, borderRadius: 2 }}>
                  <div style={{ fontSize: 11, color: "#1A56DB", fontWeight: 600, marginBottom: 6 }}>Recruiter</div>
                  <div style={{ fontSize: 10, color: "#6B7280", lineHeight: 1.6 }}>
                    ✓ Batch Evaluate . ✓ View Candidates . ✓ Validate<br/>
                    ✓ View History . ✗ Train Engine . ✗ Admin Panel
                  </div>
                </div>
                <div style={{ padding: "10px 14px", background: newUserRole === "admin" ? "#4A90E215" : "#0e0e1c", border: `1px solid ${newUserRole === "admin" ? "#4A90E240" : "#1e1e3a"}`, borderRadius: 2 }}>
                  <div style={{ fontSize: 11, color: "#4A90E2", fontWeight: 600, marginBottom: 6 }}>Admin</div>
                  <div style={{ fontSize: 10, color: "#6B7280", lineHeight: 1.6 }}>
                    ✓ Everything Recruiter can do<br/>
                    ✓ Train Engine . ✓ Library . ✓ Admin Panel
                  </div>
                </div>
              </div>

              {adminErr && <div style={{ color: "#F87171", fontSize: 11, marginBottom: 12, letterSpacing: 1 }}>✕ {adminErr}</div>}
              {adminMsg && <div style={{ color: "#1A56DB", fontSize: 11, marginBottom: 12, letterSpacing: 1 }}>{adminMsg}</div>}

              <button className="lbtn" onClick={handleCreateUser} disabled={creatingUser}>
                {creatingUser ? "Creating account..." : "Create Account →"}
              </button>
              <div style={{ fontSize: 10, color: "#6B7280", marginTop: 10, letterSpacing: 1 }}>
                The team member will receive a confirmation email. They must confirm before logging in.
              </div>
            </div>

            {/* Current users list */}
            <div>
              <div style={{ fontSize: 9, color: "#9CA3AF", letterSpacing: 3, textTransform: "uppercase", marginBottom: 16 }}>
                -- Team Members ({adminUsers.length})
              </div>
              {adminLoading ? (
                <div style={{ fontSize: 11, color: "#6B7280", letterSpacing: 1 }}>Loading users...</div>
              ) : adminUsers.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px", border: "1px dashed #1e1e3a", borderRadius: 2 }}>
                  <div style={{ fontSize: 12, color: "#9CA3AF" }}>No team members yet. Create one above.</div>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {adminUsers.map(u => (
                    <div key={u.user_id} style={{ background: "#F3F4F6", border: "1.5px solid #E5E7EB", borderRadius: 2, padding: "14px 18px", display: "flex", alignItems: "center", gap: 16 }}>
                      {/* Avatar */}
                      <div style={{ width: 36, height: 36, borderRadius: "50%", background: u.role === "admin" ? "#4A90E220" : "#00E5A015", border: `1px solid ${u.role === "admin" ? "#4A90E240" : "#BFDBFE"}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, color: u.role === "admin" ? "#4A90E2" : "#00E5A0", flexShrink: 0 }}>
                        {(u.email || "?")[0].toUpperCase()}
                      </div>
                      {/* Email */}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, color: "#111827", marginBottom: 2 }}>{u.email}</div>
                        <div style={{ fontSize: 10, color: "#6B7280" }}>
                          Added {new Date(u.created_at).toLocaleDateString()}
                          {u.user_id === user.id && <span style={{ color: "#1A56DB", marginLeft: 8 }}>. You</span>}
                        </div>
                      </div>
                      {/* Role badge */}
                      <div style={{ fontSize: 10, color: u.role === "admin" ? "#4A90E2" : "#00E5A0", background: u.role === "admin" ? "#4A90E215" : "#00E5A015", border: `1px solid ${u.role === "admin" ? "#4A90E230" : "#BFDBFE"}`, padding: "4px 12px", borderRadius: 2, letterSpacing: 1, textTransform: "uppercase" }}>
                        {u.role}
                      </div>
                      {/* Change role - can't change your own */}
                      {u.user_id !== user.id && (
                        <select
                          value={u.role}
                          onChange={e => handleChangeRole(u.user_id, e.target.value)}
                          style={{ background: "#F9FAFB", border: "1.5px solid #E5E7EB", color: "#6B7280", fontFamily: "'DM Mono',monospace", fontSize: 10, padding: "5px 10px", borderRadius: 2, cursor: "pointer", letterSpacing: 1 }}>
                          <option value="recruiter">Change → Recruiter</option>
                          <option value="admin">Change → Admin</option>
                        </select>
                      )}
                      {/* Remove - can't remove yourself */}
                      {u.user_id !== user.id && (
                        <button className="lghost"
                          onClick={() => handleRemoveUser(u.user_id, u.email)}
                          style={{ fontSize: 10, padding: "5px 12px", color: "#F87171", borderColor: "#F8717130" }}>
                          Remove
                        </button>
                      )}
                      {u.user_id === user.id && (
                        <div style={{ fontSize: 10, color: "#9CA3AF", letterSpacing: 1 }}>Owner</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Usage stats for admin */}
            <div style={{ marginTop: 32, padding: "20px 24px", background: "#F3F4F6", border: "1.5px solid #E5E7EB", borderRadius: 2 }}>
              <div style={{ fontSize: 9, color: "#6B7280", letterSpacing: 3, textTransform: "uppercase", marginBottom: 16 }}>-- App Overview</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 }}>
                {[
                  ["Training Examples", examples.length, "#00E5A0"],
                  ["Team Members", adminUsers.length, "#4A90E2"],
                  ["Auth Check", authEnabled ? "ON" : "OFF", authEnabled ? "#FACC15" : "#555"],
                  ["AI Engine", "Auto", "#8B5CF6"],
                ].map(([label, value, color]) => (
                  <div key={label} style={{ textAlign: "center", padding: "14px", background: "#ffffff", borderRadius: 2, border: "1.5px solid #E5E7EB" }}>
                    <div style={{ fontSize: 22, fontWeight: 700, color, fontFamily: "'DM Mono',monospace", marginBottom: 4 }}>{value}</div>
                    <div style={{ fontSize: 9, color: "#6B7280", letterSpacing: 1, textTransform: "uppercase" }}>{label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

      <div style={{ borderTop: "1px solid #0e0e1c", padding: "16px 40px", textAlign: "center", fontSize: 9, color: "#2a2a4a", letterSpacing: 3, textTransform: "uppercase" }}>
        Lyca 360 . Staffing Intelligence . {user.email} . <span style={{ color: isAdmin ? "#4A90E2" : "#00E5A060" }}>{userRole || "..."}</span>
      </div>
    </div>
  );
}
